import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODAL_FONT = "'IBM Plex Sans', sans-serif";

/* ─── Shared Modal Shell ─── */
export function ModalShell({ title, onClose, onOk, children, width }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: width || 500, maxHeight: "80vh", overflow: "auto", borderRadius: 6, border: "2px solid #6688CC", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
        {/* Title Bar */}
        <div style={{ background: "linear-gradient(135deg, #4A5A8A, #3A4A7A)", padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #5577AA" }}>
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15, color: "#FFF", letterSpacing: 0.5 }}>{title}</span>
          <button data-testid="modal-close" onClick={onClose} style={{ width: 22, height: 22, background: "#993333", border: "1px solid #CC4444", borderRadius: 3, color: "#FFF", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>X</button>
        </div>
        {/* Content */}
        <div style={{ background: "linear-gradient(180deg, #C8D8E8 0%, #B0C0D8 50%, #A0B0C8 100%)", padding: 20 }}>
          {children}
        </div>
        {/* Buttons */}
        <div style={{ background: "linear-gradient(180deg, #A0B0C8, #8898B8)", padding: "10px 20px", display: "flex", justifyContent: "center", gap: 12, borderTop: "1px solid #8898B8" }}>
          {onOk && <button data-testid="modal-ok" onClick={() => { onOk(); }} style={{ padding: "6px 28px", background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", cursor: "pointer" }}>Save</button>}
          <button data-testid="modal-cancel" onClick={onClose} style={{ padding: "6px 28px", background: "linear-gradient(180deg, #C8D0E0, #A0B0C8)", border: "1px solid #8899BB", borderRadius: 3, fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = { fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", minWidth: 140, textAlign: "right", paddingRight: 8 };
export const inputStyle = { flex: 1, padding: "5px 8px", border: "1px solid #99AABB", borderRadius: 2, fontFamily: MODAL_FONT, fontSize: 13, background: "#FFF", color: "#000", outline: "none" };
const rowStyle = { display: "flex", alignItems: "center", marginBottom: 10 };
const checkStyle = { display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontFamily: MODAL_FONT, fontSize: 13, color: "#333" };
const dividerStyle = { borderTop: "1px solid #8899AA", margin: "10px 0" };

/* ═══════════════════════════════
   ADD/EDIT USER
   ═══════════════════════════════ */
export function UserModal({ onClose, onSaved, agencies, currentUser }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [certs, setCerts] = useState({ MVO: false, EMT: false, PARAMEDIC: false });
  const [assigns, setAssigns] = useState({ DISPATCHER: false, AMBULANCE: false, SUPERVISOR: false });
  const [perms, setPerms] = useState({ DISPATCHER: false, SUPERVISOR: false, AGENCY_ADMIN: false });
  const [dispatcherUserid, setDispatcherUserid] = useState("");
  const [agency, setAgency] = useState("");
  const [agenciesList, setAgenciesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  // Determine if super admin (no agency or empty agency = super admin)
  const isSuperAdmin = !currentUser?.agency || currentUser.agency === "";

  useEffect(() => {
    if (agencies && agencies.length > 0) {
      setAgenciesList(isSuperAdmin ? agencies : agencies.filter(a => (currentUser?.agencies || [currentUser?.agency]).some(ua => a.name === ua)));
    } else {
      axios.get(`${API}/agencies`).then((r) => {
        setAgenciesList(isSuperAdmin ? r.data : r.data.filter(a => (currentUser?.agencies || [currentUser?.agency]).some(ua => a.name === ua)));
      }).catch(() => {});
    }
    fetchUsers();
  }, [agencies]);

  const fetchUsers = () => {
    axios.get(`${API}/users`).then((r) => {
      const filtered = isSuperAdmin ? r.data : r.data.filter(u => (currentUser?.agencies || [currentUser?.agency]).some(ua => u.agency === ua));
      // Iter 108 — alphabetize by last name, then first name so the admin
      // roster reads top-to-bottom like a phone book.
      const sorted = [...filtered].sort((a, b) => {
        const al = (a.last_name || "").toLowerCase();
        const bl = (b.last_name || "").toLowerCase();
        if (al !== bl) return al.localeCompare(bl);
        return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase());
      });
      setUsersList(sorted);
    }).catch(() => {});
  };

  const resetForm = () => {
    setFirstName(""); setLastName(""); setAgency("");
    setCerts({ MVO: false, EMT: false, PARAMEDIC: false });
    setAssigns({ DISPATCHER: false, AMBULANCE: false, SUPERVISOR: false });
    setPerms({ DISPATCHER: false, SUPERVISOR: false, AGENCY_ADMIN: false });
    setDispatcherUserid(""); setEditingId(null);
  };

  const editUser = (u) => {
    setEditingId(u.id);
    setFirstName(u.first_name || "");
    setLastName(u.last_name || "");
    setAgency(u.agency || "");
    const c = {}; (u.certifications || []).forEach(x => c[x] = true);
    setCerts({ MVO: false, EMT: false, PARAMEDIC: false, ...c });
    const a = {}; (u.assignments || []).forEach(x => a[x] = true);
    setAssigns({ DISPATCHER: false, AMBULANCE: false, SUPERVISOR: false, ...a });
    const p = {}; (u.permissions || []).forEach(x => p[x] = true);
    setPerms({ DISPATCHER: false, SUPERVISOR: false, AGENCY_ADMIN: false, ...p });
    setDispatcherUserid(u.dispatcher_userid || "");
  };

  const deleteUser = async (id) => {
    await axios.delete(`${API}/users/${id}`);
    fetchUsers();
    if (editingId === id) resetForm();
  };

  const handleOk = async () => {
    if (!firstName.trim()) return;
    const certsList = Object.keys(certs).filter((k) => certs[k]);
    const assignsList = Object.keys(assigns).filter((k) => assigns[k]);
    const permsList = Object.keys(perms).filter((k) => perms[k]);
    const payload = {
      first_name: firstName, last_name: lastName,
      certifications: certsList, assignments: assignsList,
      permissions: permsList, agency: agency,
    };
    if (perms.DISPATCHER && dispatcherUserid) {
      payload.dispatcher_userid = dispatcherUserid;
      payload.dispatcher_password = "bsvac727";
    }
    if (editingId) {
      await axios.put(`${API}/users/${editingId}`, payload);
    } else {
      await axios.post(`${API}/users`, payload);
    }
    resetForm();
    fetchUsers();
    if (onSaved) onSaved();
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const confirmed = window.confirm(
      `This will REPLACE all existing users with the contents of "${file.name}".\n\n` +
      "The built-in 'admin' account is preserved.\n\nContinue?"
    );
    if (!confirmed) { e.target.value = ""; return; }
    setUploadResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/users/upload-excel?replace=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult({ success: true, added: res.data.added, replaced: res.data.replaced });
      fetchUsers();
      resetForm();
      if (onSaved) onSaved();
    } catch {
      setUploadResult({ success: false, error: "Upload failed — check the spreadsheet format" });
    }
    e.target.value = "";
  };

  const downloadTemplate = () => {
    // Force a fresh download each click — no caching.
    window.location.href = `${API}/users/template?t=${Date.now()}`;
  };

  return (
    <ModalShell title={editingId ? "EDIT USER" : "ADD/EDIT USER"} onClose={onClose} onOk={handleOk} width={760}>
      {/* ═══════════════════════════════════════════════════════════════
          PROMINENT EXCEL UPLOAD BANNER — sits above the form so a new
          operator can immediately see / use the bulk-import workflow.
          Uploading an .xlsx REPLACES the current roster (admin is kept).
          ═══════════════════════════════════════════════════════════════ */}
      <div style={{
        marginBottom: 16, padding: "12px 14px",
        background: "linear-gradient(135deg, #2A4A7A 0%, #3A5A8A 100%)",
        border: "1px solid #4A6A9A", borderRadius: 4,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15)",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <div style={{ flex: 1, color: "#FFF", fontFamily: MODAL_FONT }}>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: 0.3 }}>
            Bulk Import Users from Excel
          </div>
          <div style={{ fontSize: 11, color: "#C8D8F0", marginTop: 2 }}>
            Uploading a spreadsheet <b>replaces</b> the current user list (admin account preserved).
          </div>
        </div>
        <button
          data-testid="user-template-download"
          type="button"
          onClick={downloadTemplate}
          style={{
            padding: "8px 14px",
            background: "linear-gradient(180deg, #E8EEF8, #B8C8D8)",
            border: "1px solid #6688AA", borderRadius: 3,
            fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 600, color: "#243",
            cursor: "pointer", whiteSpace: "nowrap",
          }}>
          Download Template
        </button>
        <label
          data-testid="user-excel-upload-label"
          style={{
            padding: "8px 16px",
            background: "linear-gradient(180deg, #52A85C, #3A8040)",
            border: "1px solid #2A6030", borderRadius: 3,
            fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 700, color: "#FFF",
            cursor: "pointer", whiteSpace: "nowrap",
            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}>
          Upload Excel File
          <input data-testid="user-excel-upload" type="file" accept=".xlsx,.xls,.csv"
            onChange={handleExcelUpload} style={{ display: "none" }} />
        </label>
      </div>
      {uploadResult && (
        <div data-testid="user-upload-result" style={{
          marginBottom: 12, padding: "8px 12px", borderRadius: 3, fontSize: 12,
          fontFamily: MODAL_FONT, fontWeight: 600,
          background: uploadResult.success ? "#D4EFD8" : "#F8D4D4",
          border: `1px solid ${uploadResult.success ? "#5EA865" : "#C05050"}`,
          color: uploadResult.success ? "#1C5024" : "#6A1010",
        }}>
          {uploadResult.success
            ? `Roster replaced — ${uploadResult.replaced || 0} removed, ${uploadResult.added} imported.`
            : uploadResult.error}
        </div>
      )}

      <div style={{ display: "flex", gap: 16 }}>
        {/* LEFT: User list */}
        <div style={{ width: 220, borderRight: "1px solid #DDD", paddingRight: 12, maxHeight: 400, overflow: "auto" }}>
          <div style={{ fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 6 }}>USERS ({usersList.length})</div>
          {usersList.map((u) => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 4px", borderBottom: "1px solid #EEE", fontSize: 11, fontFamily: MODAL_FONT, cursor: "pointer", background: editingId === u.id ? "#D8E8F8" : "transparent" }}
              onClick={() => editUser(u)}>
              <div>
                <span style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</span>
                {(u.certifications || []).length > 0 && <span style={{ color: "#0077AA", marginLeft: 4 }}>[{(u.certifications || []).join(",")}]</span>}
                <div style={{ fontSize: 10, color: "#888" }}>{u.agency ? u.agency.substring(0, 25) : "No agency"}</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); deleteUser(u.id); }}
                style={{ background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 9, cursor: "pointer", borderRadius: 2, padding: "1px 4px" }}>X</button>
            </div>
          ))}
          {usersList.length === 0 && <div style={{ color: "#888", fontSize: 11, textAlign: "center", padding: 8 }}>No users</div>}
          <div style={{ marginTop: 8 }}>
            <button onClick={resetForm} style={{ width: "100%", padding: "4px 8px", background: "#E8F0F8", border: "1px solid #AAC", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#336" }}>+ New User</button>
          </div>
        </div>

        {/* RIGHT: Form */}
        <div style={{ flex: 1 }}>
          <div style={rowStyle}>
            <span style={labelStyle}>First Name:</span>
            <input data-testid="modal-user-first" style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Last Name:</span>
            <input data-testid="modal-user-last" style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Agency:</span>
            <select data-testid="modal-user-agency" style={{ ...inputStyle, cursor: "pointer" }} value={agency} onChange={(e) => setAgency(e.target.value)}>
              <option value="">Select Agency...</option>
              {agenciesList.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </div>
          {agency && SISTER_AGENCIES_MAP[agency] && (
            <div style={{ fontFamily: MODAL_FONT, fontSize: 10, color: "#0077AA", marginTop: 2, marginBottom: 4, paddingLeft: 110 }}>
              Sister agency auto-applied: {SISTER_AGENCIES_MAP[agency]}
            </div>
          )}
          <div style={dividerStyle} />
          <div style={{ fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 6 }}>CERTIFICATIONS:</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["MVO", "EMT", "PARAMEDIC"].map((c) => (
              <label key={c} style={{ ...checkStyle, fontSize: 11 }}>
                <input type="checkbox" checked={certs[c]} onChange={(e) => setCerts({ ...certs, [c]: e.target.checked })} /> {c}
              </label>
            ))}
          </div>
          <div style={dividerStyle} />
          <div style={{ fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 6 }}>ASSIGNMENTS:</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["DISPATCHER", "AMBULANCE", "SUPERVISOR"].map((a) => (
              <label key={a} style={{ ...checkStyle, fontSize: 11 }}>
                <input type="checkbox" checked={assigns[a]} onChange={(e) => setAssigns({ ...assigns, [a]: e.target.checked })} /> {a}
              </label>
            ))}
          </div>
          <div style={dividerStyle} />
          <div style={{ fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 6 }}>PERMISSIONS:</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[{ key: "DISPATCHER", label: "DISPATCHER" }, { key: "SUPERVISOR", label: "SUPERVISOR" }, { key: "AGENCY_ADMIN", label: "AGENCY ADMIN" }].map((p) => (
              <label key={p.key} style={{ ...checkStyle, fontSize: 11 }}>
                <input type="checkbox" checked={perms[p.key]} onChange={(e) => setPerms({ ...perms, [p.key]: e.target.checked })} /> {p.label}
              </label>
            ))}
          </div>
          {perms.DISPATCHER && (
            <>
              <div style={dividerStyle} />
              <div style={rowStyle}>
                <span style={labelStyle}>User ID:</span>
                <input data-testid="modal-user-dispatcher-id" style={inputStyle} value={dispatcherUserid} onChange={(e) => setDispatcherUserid(e.target.value)} placeholder="Dispatcher login ID" />
              </div>
              <div style={{ fontFamily: MODAL_FONT, fontSize: 10, color: "#888", paddingLeft: 110 }}>Default password: bsvac727</div>
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

const SISTER_AGENCIES_MAP = {
  "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS": "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS",
  "CENTRAL BROOKLYN VOLUNTEER AMBULANCE CORPS": "BEDFORD STUYVESANT VOLUNTEER AMBULANCE CORPS",
};

/* ═══════════════════════════════
   ADD/EDIT AGENCY
   ═══════════════════════════════ */
export function AgencyModal({ onClose, onSaved }) {
  const [agenciesList, setAgenciesList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [emergencyLine, setEmergencyLine] = useState("");
  const [agencyTypes, setAgencyTypes] = useState({ BLS: false, ALS: false });
  // Operational scope — multi-select. VAC = Volunteer Ambulance Corps
  // (handles EMS units), VFD = Volunteer Fire Department (handles Fire
  // apparatus). An agency may be both.
  const [agencyScope, setAgencyScope] = useState({ VAC: false, VFD: false });
  const [boroughs, setBoroughs] = useState({});
  const [radios, setRadios] = useState("");
  const [radiosList, setRadiosList] = useState([]);
  // Iter 101 — Monthly report recipients. Free-form list of {name,email}
  // that the scheduler emails on the 1st of every month.
  const [recipients, setRecipients] = useState([]);
  const [recName, setRecName] = useState("");
  const [recEmail, setRecEmail] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("");

  const boroughList = [
    "MANHATTAN NORTH", "MANHATTAN CENTRAL", "MANHATTAN SOUTH",
    "BROOKLYN NORTH", "BROOKLYN CENTRAL", "BROOKLYN SOUTH",
    "QUEENS EAST", "QUEENS WEST",
    "BRONX NORTH", "BRONX SOUTH",
    "STATEN ISLAND",
  ];

  const fetchAgencies = () => {
    axios.get(`${API}/agencies`).then((r) => setAgenciesList(r.data)).catch(() => {});
  };
  useEffect(() => { fetchAgencies(); }, []);

  const resetForm = () => {
    setEditingId(null); setName(""); setAddress(""); setPhone("");
    setEmergencyLine(""); setAgencyTypes({ BLS: false, ALS: false });
    setAgencyScope({ VAC: false, VFD: false });
    setBoroughs({}); setRadios(""); setRadiosList([]);
    setRecipients([]); setRecName(""); setRecEmail(""); setEmailStatus("");
  };

  const loadAgency = (a) => {
    setEditingId(a.id);
    setName(a.name || "");
    setAddress(a.address || "");
    setPhone(a.phone_number || "");
    setEmergencyLine(a.emergency_line || "");
    const types = {};
    (a.service_types || []).forEach((t) => { types[t] = true; });
    setAgencyTypes({ BLS: !!types.BLS, ALS: !!types.ALS });
    const scope = {};
    (a.agency_type || []).forEach((t) => { scope[t] = true; });
    setAgencyScope({ VAC: !!scope.VAC, VFD: !!scope.VFD });
    const bMap = {};
    const coverage = a.borough_coverage || "";
    coverage.split(",").map((b) => b.trim()).filter(Boolean).forEach((b) => { bMap[b] = true; });
    setBoroughs(bMap);
    setRadiosList(a.radios || []);
    setRecipients(a.report_recipients || []);
    setEmailStatus("");
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    const serviceTypes = Object.keys(agencyTypes).filter((k) => agencyTypes[k]);
    const agencyType = Object.keys(agencyScope).filter((k) => agencyScope[k]);
    const boroughCoverage = Object.keys(boroughs).filter((k) => boroughs[k]).join(", ");
    const payload = { name, address, phone_number: phone, emergency_line: emergencyLine, service_types: serviceTypes, agency_type: agencyType, borough_coverage: boroughCoverage, radios: radiosList, report_recipients: recipients };
    if (editingId) {
      await axios.put(`${API}/agencies/${editingId}`, payload);
    } else {
      await axios.post(`${API}/agencies`, payload);
    }
    resetForm();
    fetchAgencies();
    if (onSaved) onSaved();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/agencies/${id}`);
    if (editingId === id) resetForm();
    fetchAgencies();
    if (onSaved) onSaved();
  };

  return (
    <ModalShell title="ADD/EDIT AGENCY" onClose={onClose} onOk={handleSave} width={580}>
      {/* Existing Agencies List */}
      <div style={{ maxHeight: 180, overflow: "auto", border: "1px solid #AAB8CC", borderRadius: 2, marginBottom: 12 }}>
        <div style={{ display: "flex", padding: "4px 8px", background: "#C0C8D8", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #AAB8CC" }}>
          <span style={{ flex: 2 }}>Agency Name</span>
          <span style={{ flex: 1 }}>Borough Coverage</span>
          <span style={{ width: 80 }} />
        </div>
        {agenciesList.map((a) => (
          <div key={a.id} style={{ display: "flex", padding: "4px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 12, alignItems: "center", background: editingId === a.id ? "#D0E0F0" : "transparent" }}>
            <span style={{ flex: 2, color: "#222", fontWeight: editingId === a.id ? 700 : 400 }}>{a.name}</span>
            <span style={{ flex: 1, color: "#555", fontSize: 10 }}>{a.borough_coverage || "—"}</span>
            <div style={{ width: 80, display: "flex", gap: 4 }}>
              <button data-testid={`agency-edit-${a.id}`} onClick={() => loadAgency(a)} style={{ background: "none", border: "1px solid #88AACC", color: "#3366AA", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "2px 6px" }}>Edit</button>
              <button data-testid={`agency-del-${a.id}`} onClick={() => handleDelete(a.id)} style={{ background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "2px 6px" }}>X</button>
            </div>
          </div>
        ))}
        {agenciesList.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 12, textAlign: "center" }}>No agencies</div>}
      </div>
      <div style={dividerStyle} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 8, textAlign: "center" }}>
        {editingId ? "EDITING AGENCY" : "ADD NEW AGENCY"}
        {editingId && <button onClick={resetForm} style={{ marginLeft: 10, background: "none", border: "1px solid #999", color: "#666", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "1px 8px" }}>Cancel Edit</button>}
      </div>
      <div style={rowStyle}><span style={labelStyle}>Name:</span><input data-testid="modal-agency-name" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div style={rowStyle}><span style={labelStyle}>Address:</span><input data-testid="modal-agency-address" style={inputStyle} value={address} onChange={(e) => setAddress(e.target.value)} /></div>
      <div style={rowStyle}><span style={labelStyle}>Phone Number:</span><input data-testid="modal-agency-phone" style={inputStyle} value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
      <div style={rowStyle}><span style={labelStyle}>Emergency Line:</span><input data-testid="modal-agency-eline" style={inputStyle} value={emergencyLine} onChange={(e) => setEmergencyLine(e.target.value)} /></div>
      <div style={dividerStyle} />
      <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>Agency Type:</div>
      <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
        <label style={checkStyle}><input type="checkbox" checked={agencyTypes.BLS} onChange={(e) => setAgencyTypes({ ...agencyTypes, BLS: e.target.checked })} /> BLS</label>
        <label style={checkStyle}><input type="checkbox" checked={agencyTypes.ALS} onChange={(e) => setAgencyTypes({ ...agencyTypes, ALS: e.target.checked })} /> ALS</label>
      </div>
      <div style={dividerStyle} />
      <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>Operational Scope:</div>
      <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
        <label style={checkStyle} data-testid="agency-scope-vac">
          <input type="checkbox" checked={agencyScope.VAC} onChange={(e) => setAgencyScope({ ...agencyScope, VAC: e.target.checked })} /> VAC <span style={{ fontSize: 11, color: "#666" }}>(Volunteer Ambulance Corps)</span>
        </label>
        <label style={checkStyle} data-testid="agency-scope-vfd">
          <input type="checkbox" checked={agencyScope.VFD} onChange={(e) => setAgencyScope({ ...agencyScope, VFD: e.target.checked })} /> VFD <span style={{ fontSize: 11, color: "#666" }}>(Volunteer Fire Department)</span>
        </label>
      </div>
      <div style={dividerStyle} />
      <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>Borough Coverage:</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        {boroughList.map((b) => (
          <label key={b} style={checkStyle}>
            <input type="checkbox" checked={!!boroughs[b]} onChange={(e) => setBoroughs({ ...boroughs, [b]: e.target.checked })} /> {b}
          </label>
        ))}
      </div>
      <div style={dividerStyle} />
      <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>Radios:</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
        <input data-testid="modal-agency-radio" style={{ ...inputStyle, flex: 1 }} value={radios} onChange={(e) => setRadios(e.target.value)} placeholder="Radio number" />
        <button onClick={() => { if (radios.trim()) { setRadiosList([...radiosList, radios.trim()]); setRadios(""); } }} style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: MODAL_FONT }}>Add</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {radiosList.map((r, i) => (
          <span key={i} style={{ background: "#D0D8E8", border: "1px solid #AAB8CC", borderRadius: 3, padding: "2px 8px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            {r}
            <button onClick={() => setRadiosList(radiosList.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#CC0000", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>x</button>
          </span>
        ))}
        {radiosList.length === 0 && <span style={{ fontSize: 11, color: "#888" }}>No radios added</span>}
      </div>
      <div style={dividerStyle} />
      {/* Iter 101 — Monthly report recipients section. */}
      <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>
        Monthly Report Recipients:
        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 400, color: "#666" }}>
          (auto-emailed on the 1st of each month at 08:00 NY)
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 6, marginBottom: 6 }}>
        <input data-testid="modal-agency-rec-name" style={inputStyle}
          value={recName} onChange={(e) => setRecName(e.target.value)} placeholder="Full name" />
        <input data-testid="modal-agency-rec-email" style={inputStyle} type="email"
          value={recEmail} onChange={(e) => setRecEmail(e.target.value)} placeholder="email@bsvacops.org" />
        <button
          data-testid="modal-agency-rec-add"
          onClick={() => {
            const e = recEmail.trim();
            if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
            setRecipients([...recipients, { name: recName.trim(), email: e }]);
            setRecName(""); setRecEmail("");
          }}
          style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: MODAL_FONT }}
        >
          Add
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 8 }}>
        {recipients.map((r, i) => (
          <div key={i} data-testid={`modal-agency-rec-${i}`}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 8px", background: "#EEF2F8", border: "1px solid #AAB8CC", borderRadius: 3, fontSize: 12 }}>
            <div>
              <span style={{ fontWeight: 600, color: "#222" }}>{r.name || "—"}</span>
              <span style={{ marginLeft: 8, color: "#3366AA" }}>{r.email}</span>
            </div>
            <button
              data-testid={`modal-agency-rec-remove-${i}`}
              onClick={() => setRecipients(recipients.filter((_, j) => j !== i))}
              style={{ background: "none", border: "none", color: "#CC0000", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
            >
              remove
            </button>
          </div>
        ))}
        {recipients.length === 0 && <div style={{ fontSize: 11, color: "#888" }}>No recipients configured — this agency will be skipped by the monthly job.</div>}
      </div>
      {/* Send Test Report Now */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
        <input data-testid="modal-agency-test-email" style={{ ...inputStyle, flex: 1 }} type="email"
          value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="preview to (any email)" />
        <button
          data-testid="modal-agency-test-send"
          onClick={async () => {
            if (!name.trim()) { setEmailStatus("Save the agency first."); return; }
            const t = testEmail.trim();
            if (!t) { setEmailStatus("Enter a preview email address."); return; }
            setEmailStatus("Sending…");
            try {
              const r = await axios.post(`${API}/reports/email-test`, {
                agency: name.trim(), to_email: t, range: "month",
              });
              if (r.data?.error) setEmailStatus("Error: " + r.data.error);
              else setEmailStatus(`Sent (${r.data.recipient_count} recipient, ${(r.data.size_bytes / 1024).toFixed(0)} KB PDF).`);
            } catch (e) {
              setEmailStatus("Error: " + (e?.response?.data?.detail || e.message));
            }
          }}
          style={{ background: "linear-gradient(180deg, #FFE0A0, #FFCC66)", border: "1px solid #CC9933", borderRadius: 3, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: MODAL_FONT }}
        >
          Send Test Report Now
        </button>
      </div>
      {emailStatus && (
        <div data-testid="modal-agency-email-status" style={{ fontSize: 11, color: emailStatus.startsWith("Error") ? "#CC0000" : "#0a7a3a", marginTop: 2 }}>
          {emailStatus}
        </div>
      )}
    </ModalShell>
  );
}

/* ═══════════════════════════════
   ADD/EDIT HOSPITAL
   ═══════════════════════════════ */
export function HospitalModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [erCoords, setErCoords] = useState("");
  const [notifNum, setNotifNum] = useState("");
  const [caps, setCaps] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useState(null);

  const capabilities = [
    ["Adult GED", "LVAD"], ["Critical Adult", "SAFE"], ["Critical PEDS", "Peds EDP"],
    ["OBS", "Peds Trauma"], ["Stroke", "Adult Trauma"], ["BURN", "Hyperbaric"],
    ["STEMI", "Peds GED"], ["Venom", "LVO"],
  ];

  const toggleCap = (c) => setCaps((p) => ({ ...p, [c]: !p[c] }));

  const handleOk = async () => {
    if (!name.trim()) return;
    const capsList = Object.keys(caps).filter((k) => caps[k]);
    await axios.post(`${API}/hospitals`, { name, address, er_coordinates: erCoords, notification_number: notifNum, capabilities: capsList });
    onSaved();
    onClose();
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/hospitals/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult({ success: true, count: res.data.added });
      onSaved();
    } catch (err) {
      setUploadResult({ success: false, error: err.message });
    }
    setUploading(false);
  };

  return (
    <ModalShell title="ADD/EDIT HOSPITAL" onClose={onClose} onOk={handleOk}>
      {/* Excel Upload Section */}
      <div style={{ marginBottom: 14, padding: 10, border: "2px dashed #8899BB", borderRadius: 4, background: "#E0E8F0", textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 6 }}>Upload Hospital Spreadsheet (.xlsx)</div>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>Expected columns: Name, Address, Phone/Notification Number</div>
        <input
          data-testid="hospital-excel-upload"
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleExcelUpload}
          style={{ fontSize: 12, fontFamily: MODAL_FONT }}
        />
        {uploading && <div style={{ marginTop: 6, fontSize: 12, color: "#3366AA" }}>Uploading & processing...</div>}
        {uploadResult && uploadResult.success && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#008800", fontWeight: 600 }}>
            Successfully added {uploadResult.count} hospital(s)! Locations will appear on the map.
          </div>
        )}
        {uploadResult && !uploadResult.success && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#CC0000" }}>Upload failed: {uploadResult.error}</div>
        )}
      </div>
      <div style={dividerStyle} />
      <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8, textAlign: "center" }}>Or add manually:</div>
      <div style={rowStyle}><span style={labelStyle}>Name:</span><input data-testid="modal-hosp-name" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div style={rowStyle}><span style={labelStyle}>Address:</span><input data-testid="modal-hosp-address" style={inputStyle} value={address} onChange={(e) => setAddress(e.target.value)} /></div>
      <div style={rowStyle}><span style={labelStyle}>ER Location Coordinates:</span><input data-testid="modal-hosp-coords" style={inputStyle} value={erCoords} onChange={(e) => setErCoords(e.target.value)} /></div>
      <div style={rowStyle}><span style={labelStyle}>Notification Number:</span><input data-testid="modal-hosp-notif" style={inputStyle} value={notifNum} onChange={(e) => setNotifNum(e.target.value)} /></div>
      <div style={dividerStyle} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
        {capabilities.flat().map((c) => (
          <label key={c} style={checkStyle}>
            <input type="checkbox" checked={!!caps[c]} onChange={() => toggleCap(c)} /> {c}
          </label>
        ))}
      </div>
    </ModalShell>
  );
}

/* ═══════════════════════════════
   ADD/EDIT UNITS
   ═══════════════════════════════ */
export function UnitModal({ onClose, onSaved }) {
  const [unitsList, setUnitsList] = useState([]);
  const [agenciesList, setAgenciesList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [agency, setAgency] = useState("");
  const [designation, setDesignation] = useState("");
  const [iconLabel, setIconLabel] = useState("");
  const [unitType, setUnitType] = useState("");
  // Abbreviation — 1-4 char call-sign used by the MDT command prompt to
  // resolve `UI <abbr>` / `UH <abbr>` / `ETR <abbr>` / `ASG <abbr>`.
  const [abbreviation, setAbbreviation] = useState("");
  // Fire-side: MAC addresses for auto-recognition of the Fire MDT terminal
  // mounted in this apparatus. Multiple are supported (e.g. wired NIC +
  // Wi-Fi). Only meaningful when unit_type is one of the Fire categories.
  const [macInput, setMacInput] = useState("");
  const [macList, setMacList] = useState([]);
  const [traccarDevices, setTraccarDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [loadingDevices, setLoadingDevices] = useState(true);

  const unitTypes = [
    "AMBULANCE", "EASV", "SUPERVISOR",
    "ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF",
  ];
  const FIRE_UNIT_TYPES = new Set(["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF"]);
  const isFireUnit = FIRE_UNIT_TYPES.has((unitType || "").toUpperCase());

  const fetchUnits = () => {
    axios.get(`${API}/units`).then((r) => setUnitsList(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchUnits();
    axios.get(`${API}/agencies`).then((r) => setAgenciesList(r.data)).catch(() => {});
    setLoadingDevices(true);
    axios.get(`${API}/traccar/devices`).then((r) => {
      setTraccarDevices(r.data);
    }).catch(() => {}).finally(() => setLoadingDevices(false));
  }, []);

  const statusColor = (s) => s === "online" ? "#008800" : s === "offline" ? "#CC0000" : "#888";

  const resetForm = () => {
    setEditingId(null); setAgency(""); setDesignation(""); setIconLabel("");
    setUnitType(""); setSelectedDevice(""); setAbbreviation("");
    setMacInput(""); setMacList([]);
  };

  const loadUnit = (u) => {
    setEditingId(u.id);
    setAgency(u.agency || "");
    setDesignation(u.name || "");
    setIconLabel(u.icon_label || "");
    setUnitType(u.unit_type || "");
    setAbbreviation((u.abbreviation || "").toUpperCase());
    setMacList(u.mac_addresses || []);
    // Find mapped device for this unit
    const mapped = traccarDevices.find((d) => d.mapped_unit_id === u.id);
    setSelectedDevice(mapped ? String(mapped.id) : "");
  };

  const handleSave = async () => {
    if (!designation.trim() || !agency) return;
    // Per operator spec the abbreviation is mandatory — surface a clear
    // alert before hitting the server (which also enforces the rule).
    const abbr = (abbreviation || "").trim().toUpperCase();
    if (!abbr) {
      alert("Abbreviation is required (1–4 letters/digits).");
      return;
    }
    if (!/^[A-Z0-9]{1,4}$/.test(abbr)) {
      alert("Abbreviation must be 1–4 letters or digits (no spaces or symbols).");
      return;
    }
    let unitId = editingId;

    const payload = { name: designation, unit_number: 0, agency, unit_type: unitType, response_types: [], icon_label: iconLabel.trim() || "", abbreviation: abbr, mac_addresses: macList };
    let res;
    if (editingId) {
      res = await axios.put(`${API}/units/${editingId}`, payload);
    } else {
      res = await axios.post(`${API}/units`, payload);
      unitId = res?.data?.id;
    }
    // Backend returns {error: "..."} on validation failure — surface to the dispatcher.
    if (res?.data?.error) {
      alert(res.data.error);
      return;
    }

    // Map Traccar device
    if (selectedDevice && unitId) {
      const device = traccarDevices.find((d) => String(d.id) === selectedDevice);
      if (device) {
        await axios.post(`${API}/traccar/map-device`, {
          traccar_device_id: device.id,
          traccar_device_name: device.name,
          unit_id: unitId,
          unit_name: designation,
        });
      }
    }

    resetForm();
    fetchUnits();
    // Refresh devices to update mapped status
    axios.get(`${API}/traccar/devices`).then((r) => setTraccarDevices(r.data)).catch(() => {});
    if (onSaved) onSaved();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/units/${id}`);
    if (editingId === id) resetForm();
    fetchUnits();
    if (onSaved) onSaved();
  };

  return (
    <ModalShell title="ADD/EDIT UNITS" onClose={onClose} onOk={handleSave} width={620}>
      {/* Existing Units List */}
      <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #AAB8CC", borderRadius: 2, marginBottom: 12 }}>
        <div style={{ display: "flex", padding: "4px 8px", background: "#C0C8D8", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #AAB8CC" }}>
          <span style={{ flex: 2 }}>Unit</span>
          <span style={{ width: 50 }}>Abbr</span>
          <span style={{ flex: 2 }}>Agency</span>
          <span style={{ flex: 1 }}>Type</span>
          <span style={{ width: 80 }} />
        </div>
        {unitsList.map((u) => (
          <div key={u.id} style={{ display: "flex", padding: "4px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 12, alignItems: "center", background: editingId === u.id ? "#D0E0F0" : "transparent" }}>
            <span style={{ flex: 2, color: "#222", fontWeight: editingId === u.id ? 700 : 400 }}>{u.name}</span>
            <span style={{ width: 50, color: u.abbreviation ? "#222" : "#CC0000", fontWeight: 600, fontFamily: "monospace" }}>{u.abbreviation || "—"}</span>
            <span style={{ flex: 2, color: "#555", fontSize: 11 }}>{u.agency}</span>
            <span style={{ flex: 1, color: "#555" }}>{u.unit_type || "—"}</span>
            <div style={{ width: 80, display: "flex", gap: 4 }}>
              <button data-testid={`unit-edit-${u.name}`} onClick={() => loadUnit(u)} style={{ background: "none", border: "1px solid #88AACC", color: "#3366AA", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "2px 6px" }}>Edit</button>
              <button data-testid={`unit-del-${u.name}`} onClick={() => handleDelete(u.id)} style={{ background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "2px 6px" }}>X</button>
            </div>
          </div>
        ))}
        {unitsList.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 12, textAlign: "center" }}>No units</div>}
      </div>
      <div style={dividerStyle} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#444", marginBottom: 8, textAlign: "center" }}>
        {editingId ? "EDITING UNIT" : "ADD NEW UNIT"}
        {editingId && <button onClick={resetForm} style={{ marginLeft: 10, background: "none", border: "1px solid #999", color: "#666", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "1px 8px" }}>Cancel Edit</button>}
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Agency:</span>
        <select data-testid="modal-unit-agency" value={agency} onChange={(e) => setAgency(e.target.value)}
          style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Select...</option>
          {agenciesList.map((a) => <option key={a.id || a.name} value={a.name}>{a.name}</option>)}
        </select>
      </div>
      <div style={rowStyle}><span style={labelStyle}>Unit Designation:</span><input data-testid="modal-unit-desig" style={inputStyle} value={designation} onChange={(e) => setDesignation(e.target.value)} /></div>
      <div style={rowStyle}>
        <span style={labelStyle}>Map Icon Label:</span>
        <input data-testid="modal-unit-icon" style={{ ...inputStyle, maxWidth: 80 }} value={iconLabel} onChange={(e) => setIconLabel(e.target.value.toUpperCase())} maxLength={4} placeholder="e.g. B1" />
        <span style={{ fontSize: 11, color: "#666", marginLeft: 8 }}>2-4 chars shown on map icon</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Abbreviation<span style={{ color: "#C00000" }}> *</span>:</span>
        <input
          data-testid="modal-unit-abbreviation"
          style={{ ...inputStyle, maxWidth: 80, borderColor: abbreviation ? inputStyle.borderColor : "#C00000" }}
          value={abbreviation}
          onChange={(e) => setAbbreviation(e.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 4).toUpperCase())}
          maxLength={4}
          required
          placeholder="e.g. BS3"
          title="1–4 letters or digits — used by MDT command prompt (e.g. UI BS3)"
        />
        <span style={{ fontSize: 11, color: "#666", marginLeft: 8 }}>Required — used by MDT command prompt (e.g. UI BS3)</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Unit Type:</span>
        <select data-testid="modal-unit-type" value={unitType} onChange={(e) => setUnitType(e.target.value)}
          style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Select...</option>
          <optgroup label="EMS">
            {unitTypes.filter((t) => !FIRE_UNIT_TYPES.has(t)).map((t) => <option key={t} value={t}>{t}</option>)}
          </optgroup>
          <optgroup label="Fire">
            {unitTypes.filter((t) => FIRE_UNIT_TYPES.has(t)).map((t) => <option key={t} value={t}>{t}</option>)}
          </optgroup>
        </select>
      </div>
      {/* Iter 83 — terminal binding identifiers shown for ALL units.
          Fire apparatus retain the strict-MAC-only behaviour (their MDT
          uses real-MAC binding). EMS units get the full identifier set:
          MAC, IP:1.2.3.4, or DEV:<uuid> — whatever the EMS terminal sends. */}
      <>
        <div style={dividerStyle} />
        <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>
          {isFireUnit ? "MAC Addresses" : "Terminal Identifiers"} <span style={{ fontSize: 11, color: "#666", fontWeight: 400 }}>
            {isFireUnit
              ? "— used for auto-recognition of the Fire MDT terminal on this apparatus"
              : "— used for auto-recognition of EMS MDT terminals. Accepts MAC (AA:BB:CC:DD:EE:FF), IP:1.2.3.4, or DEV:<uuid>. Add multiple to allow login from numerous computers."}
          </span>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <input
            data-testid="modal-unit-mac-input"
            style={{ ...inputStyle, flex: 1, fontFamily: "monospace" }}
            value={macInput}
            onChange={(e) => setMacInput(e.target.value.toUpperCase())}
            placeholder={isFireUnit ? "AA:BB:CC:DD:EE:FF" : "AA:BB:CC:DD:EE:FF  or  IP:10.0.0.5  or  DEV:abc-uuid"}
          />
          <button
            data-testid="modal-unit-mac-add"
            onClick={() => {
              const trimmed = macInput.trim();
              const up = trimmed.toUpperCase();
              let canonical = "";
              const hexOnly = trimmed.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
              if (hexOnly.length === 12) {
                canonical = hexOnly.match(/.{2}/g).join(":");
              } else if (up.startsWith("IP:") && trimmed.length > 3) {
                canonical = "IP:" + trimmed.slice(3).trim();
              } else if (up.startsWith("DEV:") && trimmed.length > 4) {
                canonical = "DEV:" + trimmed.slice(4).trim();
              } else {
                alert("Identifier must be a 12-hex MAC (AA:BB:CC:DD:EE:FF), IP:<addr>, or DEV:<uuid>.");
                return;
              }
              if (macList.includes(canonical)) { setMacInput(""); return; }
              setMacList([...macList, canonical]);
              setMacInput("");
            }}
            style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: MODAL_FONT }}
          >Add</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {macList.map((m, i) => {
            const isIp = m.startsWith("IP:");
            const isDev = m.startsWith("DEV:");
            const bg = isIp ? "#D0E8FF" : isDev ? "#E8D8FF" : "#FFE8C0";
            const bd = isIp ? "#5080BB" : isDev ? "#8866BB" : "#C09060";
            return (
              <span key={m} data-testid={`unit-mac-chip-${i}`} style={{ background: bg, border: `1px solid ${bd}`, borderRadius: 3, padding: "2px 8px", fontSize: 12, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 4 }}>
                {m}
                <button onClick={() => setMacList(macList.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: "#CC0000", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>x</button>
              </span>
            );
          })}
          {macList.length === 0 && <span style={{ fontSize: 11, color: "#888" }}>No identifiers added</span>}
        </div>
      </>
      <div style={dividerStyle} />
      <div style={{ fontFamily: MODAL_FONT, fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 8 }}>GPS Device (Traccar):</div>
      {loadingDevices ? (
        <div style={{ fontSize: 12, color: "#888", padding: "4px 0" }}>Loading devices...</div>
      ) : (
        <select data-testid="modal-unit-gps" value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)}
          style={{ ...inputStyle, cursor: "pointer", width: "100%" }}>
          <option value="">-- No GPS Device --</option>
          {traccarDevices.map((d) => (
            <option key={d.id} value={String(d.id)}>
              {d.name} ({d.status}){d.mapped_unit_name ? ` [mapped: ${d.mapped_unit_name}]` : ""}
            </option>
          ))}
        </select>
      )}
      {selectedDevice && (() => {
        const dev = traccarDevices.find((d) => String(d.id) === selectedDevice);
        return dev ? (
          <div style={{ marginTop: 6, fontSize: 11, color: statusColor(dev.status), fontWeight: 600 }}>
            {dev.status === "online" ? "Device Online" : dev.status === "offline" ? "Device Offline" : `Status: ${dev.status}`}
            {dev.mapped_unit_name && dev.mapped_unit_id !== editingId && <span style={{ color: "#CC6600", marginLeft: 8 }}>Currently mapped to {dev.mapped_unit_name} (will be reassigned)</span>}
          </div>
        ) : null;
      })()}
    </ModalShell>
  );
}


/* ═══════════════════════════════
   CALL TYPES (EMS or FIRE — toggled via `fire` prop)
   ═══════════════════════════════ */
export function CallTypesModal({ onClose, onSaved, fire = false }) {
  const [callTypes, setCallTypes] = useState([]);
  const [callType, setCallType] = useState("");
  const [callCode, setCallCode] = useState("");
  const [segment, setSegment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const endpoint = fire ? "fire-calltypes" : "calltypes";
  const title = fire ? "FIRE CALL TYPES" : "CALL TYPES";

  const fetch_ = () => { axios.get(`${API}/${endpoint}`).then((r) => setCallTypes(r.data)).catch(() => {}); };
  useEffect(() => { fetch_(); }, [endpoint]);

  const handleAdd = async () => {
    if (!callType.trim()) return;
    await axios.post(`${API}/${endpoint}`, { call_type: callType.trim(), call_code: callCode.trim(), segment: segment.trim() });
    setCallType(""); setCallCode(""); setSegment("");
    fetch_();
    if (onSaved) onSaved();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/${endpoint}/${id}`);
    fetch_();
    if (onSaved) onSaved();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/${endpoint}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult({ success: true, count: res.data.added });
      fetch_();
      if (onSaved) onSaved();
    } catch (err) { setUploadResult({ success: false, error: err.message }); }
    setUploading(false);
  };

  return (
    <ModalShell title={title} onClose={onClose} width={520}>
      {/* Upload */}
      <div style={{ marginBottom: 12, padding: 10, border: "2px dashed #8899BB", borderRadius: 4, background: fire ? "#FFE0CC" : "#E0E8F0", textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 4 }}>Upload {fire ? "Fire " : ""}Call Types (.xlsx)</div>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>Columns: Call Type, Call Code, Segment</div>
        <input data-testid={fire ? "fire-calltype-upload" : "calltype-upload"} type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ fontSize: 12 }} />
        {uploading && <div style={{ marginTop: 4, fontSize: 12, color: "#3366AA" }}>Processing...</div>}
        {uploadResult?.success && <div style={{ marginTop: 4, fontSize: 12, color: "#008800", fontWeight: 600 }}>Added {uploadResult.count} call type(s)</div>}
        {uploadResult && !uploadResult.success && <div style={{ marginTop: 4, fontSize: 12, color: "#CC0000" }}>Failed: {uploadResult.error}</div>}
      </div>
      <div style={dividerStyle} />
      {/* Manual add */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <input data-testid={fire ? "fire-ct-type" : "ct-type"} style={{ ...inputStyle, flex: 2 }} value={callType} onChange={(e) => setCallType(e.target.value)} placeholder="Call Type" />
        <input data-testid={fire ? "fire-ct-code" : "ct-code"} style={{ ...inputStyle, flex: 1 }} value={callCode} onChange={(e) => setCallCode(e.target.value)} placeholder="Code" />
        <input data-testid={fire ? "fire-ct-segment" : "ct-segment"} style={{ ...inputStyle, flex: 1 }} value={segment} onChange={(e) => setSegment(e.target.value)} placeholder="Segment" />
        <button data-testid={fire ? "fire-ct-add" : "ct-add"} onClick={handleAdd} style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Add</button>
      </div>
      {/* List */}
      <div style={{ maxHeight: 300, overflow: "auto", border: "1px solid #AAB8CC", borderRadius: 2 }}>
        <div style={{ display: "flex", padding: "4px 8px", background: "#C0C8D8", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #AAB8CC" }}>
          <span style={{ flex: 2 }}>Call Type</span><span style={{ flex: 1 }}>Code</span><span style={{ flex: 1 }}>Segment</span><span style={{ width: 50 }} />
        </div>
        {callTypes.map((ct) => (
          <div key={ct.id} style={{ display: "flex", padding: "4px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 12, alignItems: "center" }}>
            <span style={{ flex: 2, color: "#222" }}>{ct.call_type}</span>
            <span style={{ flex: 1, color: "#555" }}>{ct.call_code}</span>
            <span style={{ flex: 1, color: "#555" }}>{ct.segment}</span>
            <button onClick={() => handleDelete(ct.id)} style={{ width: 40, background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 10, cursor: "pointer", borderRadius: 2 }}>X</button>
          </div>
        ))}
        {callTypes.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 12, textAlign: "center" }}>No call types added</div>}
      </div>
    </ModalShell>
  );
}


/* Convenience wrapper — Fire variant for the Admin menu. */
export function FireCallTypesModal(props) {
  return <CallTypesModal {...props} fire />;
}


/* ═══════════════════════════════
   FORCE LOG OFF (ALL or selected)
   Operator can pick specific units to log off, or use "Select All" to
   replicate the legacy nuke-everything behavior.
   ═══════════════════════════════ */
export function ForceLogoffModal({ onClose, onDone }) {
  const [units, setUnits] = useState([]);
  const [selected, setSelected] = useState({}); // { unit_id: bool }
  const [stage, setStage] = useState("pick"); // pick | confirm | submitting
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/units`);
        // Only logged-in units are eligible — logged-off ones don't need force action.
        const live = (r.data || []).filter((u) => u.logged_in);
        setUnits(live);
      } catch {
        setUnits([]);
      }
    })();
  }, []);

  const allChecked = units.length > 0 && units.every((u) => selected[u.id]);
  const someChecked = units.some((u) => selected[u.id]);
  const checkedCount = units.filter((u) => selected[u.id]).length;

  const toggleAll = () => {
    if (allChecked) setSelected({});
    else {
      const next = {};
      units.forEach((u) => { next[u.id] = true; });
      setSelected(next);
    }
  };

  const handleProceed = () => {
    if (!someChecked) { setError("Select at least one unit, or check Select All."); return; }
    setError("");
    setStage("confirm");
  };

  const handleConfirm = async () => {
    setStage("submitting");
    try {
      const ids = units.filter((u) => selected[u.id]).map((u) => u.id);
      const body = allChecked ? {} : { unit_ids: ids };
      const r = await axios.post(`${API}/admin/force-logoff-all`, body);
      setResult(r.data);
      if (onDone) onDone();
    } catch (err) {
      setError(err.message || "Force logoff failed");
      setStage("confirm");
    }
  };

  const containerStyle = { maxHeight: 320, overflow: "auto", border: "1px solid #AAB8CC", borderRadius: 2, background: "#FFF" };

  return (
    <ModalShell title="FORCE LOG OFF UNITS" onClose={onClose} width={560}>
      {result ? (
        <div data-testid="force-logoff-result" style={{ padding: 16, background: "#E8F4E8", border: "1px solid #88AA88", borderRadius: 4, color: "#225522" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Done.</div>
          <div style={{ fontSize: 12 }}>{result.units_logged_off} unit(s) logged off, {result.jobs_cancelled} job(s) cancelled.</div>
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <button data-testid="force-logoff-close" onClick={onClose} style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "5px 18px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Close</button>
          </div>
        </div>
      ) : stage === "confirm" || stage === "submitting" ? (
        <div data-testid="force-logoff-confirm" style={{ padding: 16, background: "#FFF0E0", border: "1px solid #CC8844", borderRadius: 4, color: "#553311" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: "#AA2222" }}>
            CONFIRM — this will log off {allChecked ? "EVERY logged-in unit" : `${checkedCount} unit(s)`} and cancel any active job(s) assigned to {allChecked ? "any of them" : "those units"}.
          </div>
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            {units.filter((u) => selected[u.id]).map((u) => u.name).join(", ") || "(none)"}
          </div>
          <div style={{ fontSize: 11, color: "#774422" }}>This cannot be undone.</div>
          {error && <div style={{ color: "#CC0000", fontSize: 11, marginTop: 8 }}>{error}</div>}
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button
              data-testid="force-logoff-back"
              disabled={stage === "submitting"}
              onClick={() => setStage("pick")}
              style={{ background: "linear-gradient(180deg, #C8D0E0, #A0B0C8)", border: "1px solid #8899BB", borderRadius: 3, padding: "5px 16px", fontSize: 12, fontWeight: 600, cursor: stage === "submitting" ? "not-allowed" : "pointer" }}
            >
              Back
            </button>
            <button
              data-testid="force-logoff-confirm-btn"
              disabled={stage === "submitting"}
              onClick={handleConfirm}
              style={{ background: "linear-gradient(180deg, #E89090, #C04040)", border: "1px solid #882222", borderRadius: 3, padding: "5px 18px", fontSize: 12, fontWeight: 700, color: "#FFF", cursor: stage === "submitting" ? "wait" : "pointer" }}
            >
              {stage === "submitting" ? "Working…" : "YES — Log Off"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 10, fontSize: 12, color: "#222" }}>
            Pick the units to force off. Each selected unit gets archived, reset to signal 97, and any active jobs assigned to them are cancelled.
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#D8DFE8", border: "1px solid #8899BB", borderRadius: 3, marginBottom: 8, cursor: units.length ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 600, color: "#222" }}>
            <input
              data-testid="force-logoff-select-all"
              type="checkbox"
              checked={allChecked}
              disabled={units.length === 0}
              onChange={toggleAll}
            />
            Select All ({units.length} logged-in unit{units.length === 1 ? "" : "s"})
          </label>

          <div style={containerStyle}>
            {units.length === 0 && (
              <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: "#888" }}>No units are currently logged in.</div>
            )}
            {units.map((u) => {
              const sig = u.signal === 0 ? "NOSTAT" : u.signal === 10 ? "DISP" : (u.signal ?? 97);
              const onCall = !!u.current_job_id;
              return (
                <label
                  key={u.id}
                  data-testid={`force-logoff-row-${u.name}`}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "1px solid #E0E4EC", fontSize: 12, color: "#222", cursor: "pointer" }}
                >
                  <input
                    data-testid={`force-logoff-cb-${u.name}`}
                    type="checkbox"
                    checked={!!selected[u.id]}
                    onChange={(e) => setSelected((p) => ({ ...p, [u.id]: e.target.checked }))}
                  />
                  <span style={{ minWidth: 110, fontWeight: 700 }}>{u.name}</span>
                  <span style={{ minWidth: 70, color: "#666" }}>{u.unit_type || ""}</span>
                  <span style={{ minWidth: 50, color: "#444", fontFamily: "'IBM Plex Mono', monospace" }}>{sig}</span>
                  <span style={{ flex: 1, color: "#666", fontSize: 11 }}>{u.agency || ""}</span>
                  {onCall && (
                    <span style={{ background: "#FFCC00", color: "#553300", fontSize: 9, fontWeight: 800, padding: "1px 4px", borderRadius: 2 }}>
                      ON CALL
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {error && <div style={{ color: "#CC0000", fontSize: 12, marginTop: 8 }}>{error}</div>}

          <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#444" }}>{checkedCount} of {units.length} selected</span>
            <button
              data-testid="force-logoff-proceed"
              onClick={handleProceed}
              disabled={!someChecked}
              style={{ background: someChecked ? "linear-gradient(180deg, #E89090, #C04040)" : "#BBB", border: "1px solid #882222", borderRadius: 3, padding: "5px 18px", fontSize: 12, fontWeight: 700, color: "#FFF", cursor: someChecked ? "pointer" : "not-allowed" }}
            >
              Force Log Off…
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}


/* ═══════════════════════════════
   CALL TYPE MAPPINGS (Fire ↔ EMS)
   Operator spec: cross-service assignments need an automatic call-type
   translation. e.g. when an EMS unit is dispatched to a FIRE box-job, the
   call_info gets stamped with the EMS-equivalent call type. This modal
   manages the bidirectional mapping table.
   ═══════════════════════════════ */
export function CallTypeMappingsModal({ onClose }) {
  const [mappings, setMappings] = useState([]);
  const [emsTypes, setEmsTypes] = useState([]);
  const [fireTypes, setFireTypes] = useState([]);
  const [fireType, setFireType] = useState("");
  const [emsType, setEmsType] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState("");
  // Inline upload-status banner (cleared after every successful refetch)
  // — keeps the operator in this single modal even when refreshing the
  // source catalogs.
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadingKind, setUploadingKind] = useState(""); // "ems" | "fire" | ""

  const fetch_ = async () => {
    try {
      const [m, e, f] = await Promise.all([
        axios.get(`${API}/call-type-mappings`),
        axios.get(`${API}/calltypes`),
        axios.get(`${API}/fire-calltypes`),
      ]);
      setMappings(m.data || []);
      setEmsTypes((e.data || []).map((c) => c.call_type).filter(Boolean));
      setFireTypes((f.data || []).map((c) => c.call_type).filter(Boolean));
    } catch {
      // ignore — banner will show "Failed to add" if the backend is unreachable
    }
  };
  useEffect(() => { fetch_(); }, []);

  const handleAdd = async () => {
    setError("");
    if (!fireType.trim() || !emsType.trim()) { setError("Both Fire and EMS call types are required."); return; }
    try {
      const r = await axios.post(`${API}/call-type-mappings`, {
        fire_type: fireType.trim(),
        ems_type: emsType.trim(),
        is_default: isDefault,
      });
      if (r.data?.error) { setError(r.data.error); return; }
      setFireType(""); setEmsType(""); setIsDefault(false);
      fetch_();
    } catch (err) { setError(err.message || "Failed to add mapping"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/call-type-mappings/${id}`);
      fetch_();
    } catch {
      // ignore
    }
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    setUploadStatus("");
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = kind === "fire"
        ? `${API}/fire-calltypes/upload`
        : `${API}/calltypes/upload`;
      const r = await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const added = r.data?.added ?? 0;
      const label = kind === "fire" ? "Fire" : "EMS";
      setUploadStatus(`${label}: added ${added} call type${added === 1 ? "" : "s"} from ${file.name}.`);
      fetch_();
    } catch (err) {
      setUploadStatus(`Upload failed: ${err.message || "unknown error"}`);
    } finally {
      setUploadingKind("");
    }
  };

  return (
    <ModalShell title="CALL TYPE MAPPINGS (FIRE ↔ EMS)" onClose={onClose} width={640}>
      {/* Help banner */}
      <div style={{ marginBottom: 12, padding: 10, background: "#FFE8CC", border: "1px solid #CC9966", borderRadius: 4, fontSize: 11, color: "#553311", lineHeight: 1.4 }}>
        Defines how a call type translates when a unit from the OTHER service is dispatched.<br />
        e.g. an EMS unit assigned to a FIRE box-job → the EMS-equivalent call type is auto-stamped to call_info.<br />
        Mark a row as <strong>DEFAULT</strong> to make it the canonical fallback when more than one mapping exists for a given source type.
      </div>

      {/* Excel upload — refresh the source catalogs without leaving this
          modal. Uploaded entries become available immediately in the Fire
          and EMS autocomplete dropdowns below. Per operator request:
          two distinct upload areas, one for each service. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        {/* EMS upload */}
        <div style={{ flex: 1, padding: 8, background: "#E8F0FF", border: "1px solid #88AACC", borderRadius: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#22558A", marginBottom: 4 }}>
            EMS CALL TYPES &mdash; current count: {emsTypes.length}
          </div>
          <label
            data-testid="ctm-upload-ems-label"
            style={{ display: "inline-block", background: "linear-gradient(180deg, #C8DDF8, #95B6E0)", border: "1px solid #4A6E9A", borderRadius: 3, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: uploadingKind === "ems" ? "wait" : "pointer", color: "#1B3A60" }}
          >
            {uploadingKind === "ems" ? "Uploading…" : "Upload EMS .xlsx"}
            <input
              data-testid="ctm-upload-ems"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              disabled={uploadingKind === "ems"}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";  // allow re-uploading the same file
                if (f) handleUpload("ems", f);
              }}
            />
          </label>
          <div style={{ fontSize: 10, color: "#445588", marginTop: 4 }}>
            Columns: Call Type, Call Code, Segment.
          </div>
        </div>

        {/* Fire upload */}
        <div style={{ flex: 1, padding: 8, background: "#FFE8DD", border: "1px solid #CC8866", borderRadius: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#AA4422", marginBottom: 4 }}>
            FIRE CALL TYPES &mdash; current count: {fireTypes.length}
          </div>
          <label
            data-testid="ctm-upload-fire-label"
            style={{ display: "inline-block", background: "linear-gradient(180deg, #F8D8C8, #E0A595)", border: "1px solid #8A4A2A", borderRadius: 3, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: uploadingKind === "fire" ? "wait" : "pointer", color: "#5A2A10" }}
          >
            {uploadingKind === "fire" ? "Uploading…" : "Upload Fire .xlsx"}
            <input
              data-testid="ctm-upload-fire"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              disabled={uploadingKind === "fire"}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) handleUpload("fire", f);
              }}
            />
          </label>
          <div style={{ fontSize: 10, color: "#883322", marginTop: 4 }}>
            Columns: Call Type, Call Code, Segment.
          </div>
        </div>
      </div>

      {/* Upload result banner — cleared on next upload */}
      {uploadStatus && (
        <div data-testid="ctm-upload-status" style={{ marginBottom: 8, padding: 6, background: "#EEFFEE", border: "1px solid #88BB88", borderRadius: 3, fontSize: 11, color: "#225522" }}>
          {uploadStatus}
        </div>
      )}

      {/* Add form */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "center" }}>
        <input
          data-testid="ctm-fire-type"
          list="ctm-fire-list"
          style={{ ...inputStyle, flex: 2 }}
          value={fireType}
          onChange={(e) => setFireType(e.target.value.toUpperCase())}
          placeholder="Fire Call Type"
        />
        <datalist id="ctm-fire-list">
          {fireTypes.map((t) => <option key={t} value={t} />)}
        </datalist>
        <span style={{ color: "#333", fontWeight: 700 }}>↔</span>
        <input
          data-testid="ctm-ems-type"
          list="ctm-ems-list"
          style={{ ...inputStyle, flex: 2 }}
          value={emsType}
          onChange={(e) => setEmsType(e.target.value.toUpperCase())}
          placeholder="EMS Call Type"
        />
        <datalist id="ctm-ems-list">
          {emsTypes.map((t) => <option key={t} value={t} />)}
        </datalist>
        <label style={{ ...checkStyle, marginBottom: 0, fontSize: 11 }}>
          <input data-testid="ctm-default" type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Default
        </label>
        <button
          data-testid="ctm-add"
          onClick={handleAdd}
          style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
        >
          Add
        </button>
      </div>
      {error && <div style={{ color: "#CC0000", fontSize: 11, marginBottom: 8 }}>{error}</div>}

      {/* List */}
      <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #AAB8CC", borderRadius: 2 }}>
        <div style={{ display: "flex", padding: "4px 8px", background: "#C0C8D8", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #AAB8CC" }}>
          <span style={{ flex: 2 }}>FIRE</span>
          <span style={{ width: 30, textAlign: "center" }}>↔</span>
          <span style={{ flex: 2 }}>EMS</span>
          <span style={{ width: 70, textAlign: "center" }}>Default</span>
          <span style={{ width: 50 }} />
        </div>
        {mappings.map((m) => (
          <div key={m.id} data-testid={`ctm-row-${m.id}`} style={{ display: "flex", padding: "4px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 12, alignItems: "center" }}>
            <span style={{ flex: 2, color: "#AA4422", fontWeight: 600 }}>{m.fire_type}</span>
            <span style={{ width: 30, textAlign: "center", color: "#555" }}>↔</span>
            <span style={{ flex: 2, color: "#22558A", fontWeight: 600 }}>{m.ems_type}</span>
            <span style={{ width: 70, textAlign: "center", fontWeight: 700, color: m.is_default ? "#008800" : "#999" }}>
              {m.is_default ? "DEFAULT" : "—"}
            </span>
            <button
              data-testid={`ctm-delete-${m.id}`}
              onClick={() => handleDelete(m.id)}
              style={{ width: 40, background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 10, cursor: "pointer", borderRadius: 2 }}
            >
              X
            </button>
          </div>
        ))}
        {mappings.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 12, textAlign: "center" }}>No mappings configured. Cross-service assignments will fall back to the source call type.</div>}
      </div>
    </ModalShell>
  );
}


/* ═══════════════════════════════
   ADD/EDIT CSL (Cross Street Locations)
   ═══════════════════════════════ */
export function CSLModal({ onClose, onSaved }) {
  const [csls, setCsls] = useState([]);
  const [cslName, setCslName] = useState("");
  const [cslBorough, setCslBorough] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const boroughList = [
    "MANHATTAN NORTH", "MANHATTAN CENTRAL", "MANHATTAN SOUTH",
    "BROOKLYN NORTH", "BROOKLYN CENTRAL", "BROOKLYN SOUTH",
    "QUEENS EAST", "QUEENS WEST",
    "BRONX NORTH", "BRONX SOUTH",
    "STATEN ISLAND",
  ];

  const fetchCSLs = () => {
    axios.get(`${API}/csl`).then((r) => setCsls(r.data)).catch(() => {});
  };
  useEffect(() => { fetchCSLs(); }, []);

  const handleAdd = async () => {
    if (!cslName.trim()) return;
    await axios.post(`${API}/csl`, { name: cslName.trim(), boundaries: [], borough_coverage: cslBorough });
    setCslName("");
    setCslBorough("");
    fetchCSLs();
    if (onSaved) onSaved();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/csl/${id}`);
    fetchCSLs();
    if (onSaved) onSaved();
  };

  const handleKMLUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".kml")) {
      setUploadResult({ success: false, error: "Only .kml files are accepted" });
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/csl/upload-kml`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult({ success: true, count: res.data.added });
      fetchCSLs();
      if (onSaved) onSaved();
    } catch (err) {
      setUploadResult({ success: false, error: err.message });
    }
    setUploading(false);
  };

  const handleBoroughChange = async (cslId, newBorough) => {
    const csl = csls.find((c) => c.id === cslId);
    if (!csl) return;
    await axios.put(`${API}/csl/${cslId}`, { name: csl.name, boundaries: [], borough_coverage: newBorough });
    fetchCSLs();
    if (onSaved) onSaved();
  };

  return (
    <ModalShell title="ADD/EDIT CSL (CROSS STREET LOCATIONS)" onClose={onClose} width={600}>
      {/* KML Upload */}
      <div style={{ marginBottom: 14, padding: 10, border: "2px dashed #8899BB", borderRadius: 4, background: "#E0E8F0", textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 4 }}>Upload KML Boundary File (.kml)</div>
        <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>KML files define geographic boundaries for cross street locations</div>
        <input
          data-testid="csl-kml-upload"
          type="file"
          accept=".kml"
          onChange={handleKMLUpload}
          style={{ fontSize: 12, fontFamily: MODAL_FONT }}
        />
        {uploading && <div style={{ marginTop: 4, fontSize: 12, color: "#3366AA" }}>Parsing KML...</div>}
        {uploadResult?.success && (
          <div style={{ marginTop: 4, fontSize: 12, color: "#008800", fontWeight: 600 }}>
            Added {uploadResult.count} cross street location(s) from KML
          </div>
        )}
        {uploadResult && !uploadResult.success && (
          <div style={{ marginTop: 4, fontSize: 12, color: "#CC0000" }}>Failed: {uploadResult.error}</div>
        )}
      </div>
      <div style={dividerStyle} />
      {/* Manual add */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8, textAlign: "center" }}>Or add manually:</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <input data-testid="csl-name-input" style={{ ...inputStyle, flex: 1 }} value={cslName} onChange={(e) => setCslName(e.target.value)} placeholder="Cross Street Location Name" />
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#333", minWidth: 110 }}>Borough Coverage:</span>
        <select data-testid="csl-borough" value={cslBorough} onChange={(e) => setCslBorough(e.target.value)}
          style={{ ...inputStyle, cursor: "pointer" }}>
          <option value="">Select...</option>
          {boroughList.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <button data-testid="csl-add-btn" onClick={handleAdd} style={{ background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: MODAL_FONT, whiteSpace: "nowrap" }}>Add</button>
      </div>
      {/* List */}
      <div style={{ maxHeight: 300, overflow: "auto", border: "1px solid #AAB8CC", borderRadius: 2 }}>
        <div style={{ display: "flex", padding: "4px 8px", background: "#C0C8D8", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #AAB8CC" }}>
          <span style={{ flex: 2 }}>Name</span>
          <span style={{ flex: 1 }}>Borough</span>
          <span style={{ flex: 1 }}>Boundaries</span>
          <span style={{ width: 40 }} />
        </div>
        {csls.map((c) => (
          <div key={c.id} style={{ display: "flex", padding: "4px 8px", borderBottom: "1px solid #E0E4EC", fontSize: 12, alignItems: "center" }}>
            <span style={{ flex: 2, color: "#222" }}>{c.name}</span>
            <span style={{ flex: 1 }}>
              <select value={c.borough_coverage || ""} onChange={(e) => handleBoroughChange(c.id, e.target.value)}
                style={{ width: "100%", padding: "2px 4px", border: "1px solid #AAB8CC", borderRadius: 2, fontSize: 11, background: c.borough_coverage ? "#FFF" : "#FFEEDD", color: "#000", cursor: "pointer" }}>
                <option value="">Select Borough...</option>
                {boroughList.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </span>
            <span style={{ flex: 0.5, color: "#555", textAlign: "center" }}>{c.boundaries?.length || 0} pts</span>
            <button onClick={() => handleDelete(c.id)} style={{ width: 40, background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 10, cursor: "pointer", borderRadius: 2, marginLeft: 4 }}>X</button>
          </div>
        ))}
        {csls.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 12, textAlign: "center" }}>No cross street locations added</div>}
      </div>
    </ModalShell>
  );
}


/* ─── KML Boundaries Modal (Borough + Police PCT + VAC Response Area) ─── */
export function KMLBoundaryModal({ onClose, onSaved }) {
  const [boroughs, setBoroughs] = useState([]);
  const [pcts, setPcts] = useState([]);
  const [vacResponse, setVacResponse] = useState([]);
  const [parZones, setParZones] = useState([]);     // 10-97 PAR zone polygons
  const [agencies, setAgencies] = useState([]);     // for PAR assignment selector
  const [parAssignments, setParAssignments] = useState({});  // {agency_id: [zone_id,...]}
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // Inline-rename state: { id, value } — only one row is edited at a time.
  const [editing, setEditing] = useState(null);

  const fetchData = () => {
    axios.get(`${API}/kml/boroughs`).then((r) => setBoroughs(r.data)).catch(() => {});
    axios.get(`${API}/kml/pcts`).then((r) => setPcts(r.data)).catch(() => {});
    axios.get(`${API}/kml/vac-response`).then((r) => setVacResponse(r.data)).catch(() => {});
    axios.get(`${API}/par-zones`).then((r) => setParZones(r.data)).catch(() => {});
    axios.get(`${API}/agencies`).then((r) => {
      setAgencies(r.data);
      // Pre-load each agency's current PAR assignment.
      Promise.all(
        r.data.map((a) => axios.get(`${API}/agencies/${a.id}/par`).then((res) => [a.id, res.data.par_zone_ids || []]).catch(() => [a.id, []]))
      ).then((entries) => {
        const map = {};
        entries.forEach(([id, ids]) => { map[id] = ids; });
        setParAssignments(map);
      });
    }).catch(() => {});
  };
  useEffect(() => { fetchData(); }, []);

  const handleUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fname = file.name.toLowerCase();
    if (!fname.endsWith(".kml") && !fname.endsWith(".kmz") && !fname.endsWith(".geojson") && !fname.endsWith(".json")) {
      setUploadResult({ success: false, error: "Only .kml, .kmz and .geojson files are accepted" });
      return;
    }
    setUploading(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/kml/upload/${type}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.error) {
        setUploadResult({ success: false, error: res.data.error });
      } else {
        setUploadResult({ success: true, count: res.data.added, type });
      }
      fetchData();
      if (onSaved) onSaved();
    } catch {
      setUploadResult({ success: false, error: "Upload failed" });
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/kml/${id}`);
    fetchData();
  };

  const handleDeleteAll = async (type) => {
    await axios.delete(`${API}/kml/type/${type}`);
    fetchData();
  };

  // ─── Inline rename helpers ────────────────────────────────────────────
  // Clicking a row's ✎ button moves that row into edit mode; Enter / blur /
  // Save commits the new name via PUT /api/kml/{id}; Esc cancels.
  const startEdit = (item) => setEditing({ id: item.id, value: item.name || "" });
  const cancelEdit = () => setEditing(null);
  const commitEdit = async () => {
    if (!editing) return;
    const newName = (editing.value || "").trim();
    if (!newName) { setEditing(null); return; }
    try {
      await axios.put(`${API}/kml/${editing.id}`, { name: newName });
      fetchData();
      if (onSaved) onSaved();
    } catch {}
    setEditing(null);
  };
  const handleMove = async (item, targetType) => {
    // Re-classify a boundary between borough / pct / vac_response.
    try {
      await axios.put(`${API}/kml/${item.id}`, { type: targetType });
      fetchData();
      if (onSaved) onSaved();
    } catch {}
  };

  const sectionStyle = { fontFamily: MODAL_FONT, fontSize: 14, fontWeight: 700, color: "#333", marginBottom: 8 };
  const descStyle = { fontSize: 11, color: "#666", marginBottom: 6 };
  const listStyle = { maxHeight: 160, overflow: "auto", border: "1px solid #DDD", borderRadius: 4, marginBottom: 16, background: "#FFF" };
  const itemStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 10px", borderBottom: "1px solid #EEE", fontSize: 12, fontFamily: MODAL_FONT, gap: 6 };
  const delBtn = { background: "none", border: "1px solid #CCC", color: "#CC0000", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "2px 6px" };
  const editBtn = { background: "#E8EEF8", border: "1px solid #99B", color: "#334", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "2px 8px", fontWeight: 600 };
  const saveBtn = { background: "linear-gradient(180deg, #52A85C, #2E7A3A)", border: "1px solid #1E5A24", color: "#FFF", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "3px 10px", fontWeight: 700 };
  const cancelBtn = { background: "#DDE2EA", border: "1px solid #99AABB", color: "#345", fontSize: 10, cursor: "pointer", borderRadius: 2, padding: "3px 8px" };

  // Row renderer shared across the three boundary sections.
  const renderBoundaryRow = (item, currentType) => {
    const isEditing = editing && editing.id === item.id;
    return (
      <div key={item.id} data-testid={`kml-row-${item.id}`} style={itemStyle}>
        {isEditing ? (
          <>
            <input
              data-testid={`kml-edit-input-${item.id}`}
              autoFocus
              value={editing.value}
              onChange={(e) => setEditing({ ...editing, value: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); else if (e.key === "Escape") cancelEdit(); }}
              style={{ flex: 1, padding: "3px 6px", border: "1px solid #88A", borderRadius: 2, fontFamily: MODAL_FONT, fontSize: 12 }}
            />
            <button data-testid={`kml-edit-save-${item.id}`} type="button" onClick={commitEdit} style={saveBtn}>Save</button>
            <button data-testid={`kml-edit-cancel-${item.id}`} type="button" onClick={cancelEdit} style={cancelBtn}>Cancel</button>
          </>
        ) : (
          <>
            <span style={{ fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.name}>{item.name}</span>
            <select
              data-testid={`kml-move-select-${item.id}`}
              value={currentType}
              onChange={(e) => { if (e.target.value !== currentType) handleMove(item, e.target.value); }}
              style={{ fontSize: 10, padding: "2px 4px", border: "1px solid #BBB", borderRadius: 2, background: "#FFF", color: "#334" }}
              title="Move to another boundary type"
            >
              <option value="borough">Borough</option>
              <option value="pct">Precinct</option>
              <option value="vac_response">VAC Response</option>
            </select>
            <button data-testid={`kml-edit-btn-${item.id}`} type="button" onClick={() => startEdit(item)} style={editBtn}>Rename</button>
            <button data-testid={`kml-delete-btn-${item.id}`} type="button" onClick={() => handleDelete(item.id)} style={delBtn}>X</button>
          </>
        )}
      </div>
    );
  };

  return (
    <ModalShell title="KML / GEOJSON BOUNDARIES" onClose={onClose} onOk={onClose} width={620}>
      {/* Borough */}
      <div style={sectionStyle}>BOROUGH BOUNDARIES</div>
      <div style={descStyle}>Upload KML/KMZ/GeoJSON for borough boundaries. Data extraction only (no map overlay).</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <input data-testid="kml-borough-upload" type="file" accept=".kml,.kmz,.geojson,.json" onChange={(e) => handleUpload(e, "borough")} style={{ fontFamily: MODAL_FONT, fontSize: 12 }} />
        {boroughs.length > 0 && <button data-testid="delete-all-boroughs" onClick={() => handleDeleteAll("borough")} style={{ ...delBtn, padding: "3px 10px" }}>Remove All</button>}
      </div>
      <div style={listStyle}>
        {boroughs.map((b) => renderBoundaryRow(b, "borough"))}
        {boroughs.length === 0 && <div style={{ padding: 8, color: "#888", fontSize: 12, textAlign: "center" }}>No borough boundaries uploaded</div>}
      </div>

      <div style={dividerStyle} />

      {/* Police PCT */}
      <div style={sectionStyle}>POLICE PRECINCT BOUNDARIES</div>
      <div style={descStyle}>Upload KML/KMZ/GeoJSON for Police Precinct boundaries. Light orange overlay on map.</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <input data-testid="kml-pct-upload" type="file" accept=".kml,.kmz,.geojson,.json" onChange={(e) => handleUpload(e, "pct")} style={{ fontFamily: MODAL_FONT, fontSize: 12 }} />
        {pcts.length > 0 && <button data-testid="delete-all-pcts" onClick={() => handleDeleteAll("pct")} style={{ ...delBtn, padding: "3px 10px" }}>Remove All</button>}
      </div>
      <div style={listStyle}>
        {pcts.map((p) => renderBoundaryRow(p, "pct"))}
        {pcts.length === 0 && <div style={{ padding: 8, color: "#888", fontSize: 12, textAlign: "center" }}>No precinct boundaries uploaded</div>}
      </div>

      <div style={dividerStyle} />

      {/* VAC Response Area */}
      <div style={sectionStyle}>VAC RESPONSE AREA</div>
      <div style={descStyle}>Upload KML/KMZ/GeoJSON for VAC Response Areas. Light border on map. Uploading new file replaces existing data.</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <input data-testid="kml-vac-upload" type="file" accept=".kml,.kmz,.geojson,.json" onChange={(e) => handleUpload(e, "vac_response")} style={{ fontFamily: MODAL_FONT, fontSize: 12 }} />
        {vacResponse.length > 0 && <button data-testid="delete-all-vac" onClick={() => handleDeleteAll("vac_response")} style={{ ...delBtn, padding: "3px 10px" }}>Remove All</button>}
      </div>
      <div style={listStyle}>
        {vacResponse.map((v) => renderBoundaryRow(v, "vac_response"))}
        {vacResponse.length === 0 && <div style={{ padding: 8, color: "#888", fontSize: 12, textAlign: "center" }}>No VAC response areas uploaded</div>}
      </div>

      <div style={dividerStyle} />

      {/* 10-97 PAR (Primary Area of Responsibility) */}
      <div style={sectionStyle}>10-97 PAR (PRIMARY AREA OF RESPONSIBILITY)</div>
      <div style={descStyle}>
        Upload zone polygons that define each agency's 10-97 area. When a logged-in unit goes 10-98 (returning) and its GPS sits inside any of its agency's assigned zones, the system auto-flips them to 10-97. The same KML can carry multiple zones — each agency below cherry-picks the zones that make up its PAR.
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <input data-testid="kml-par-upload" type="file" accept=".kml,.kmz,.geojson,.json" onChange={(e) => handleUpload(e, "par")} style={{ fontFamily: MODAL_FONT, fontSize: 12 }} />
        {parZones.length > 0 && <button data-testid="delete-all-par" onClick={() => handleDeleteAll("par")} style={{ ...delBtn, padding: "3px 10px" }}>Remove All</button>}
      </div>
      <div style={listStyle}>
        {parZones.map((p) => renderBoundaryRow(p, "par"))}
        {parZones.length === 0 && <div style={{ padding: 8, color: "#888", fontSize: 12, textAlign: "center" }}>No PAR zones uploaded</div>}
      </div>

      {/* Agency → PAR-zone assignment matrix. Renders a checkbox per
          (agency, uploaded zone) cell. Saved on every checkbox toggle.
          Per operator spec — the same agency can pick zones from MULTIPLE
          uploaded files, so we group columns under their source filename
          for clarity. */}
      {parZones.length > 0 && agencies.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div style={{ ...sectionStyle, fontSize: 12, marginTop: 6 }}>ASSIGN ZONES TO AGENCIES</div>
          <div style={{ overflowX: "auto", border: "1px solid #CCD", borderRadius: 3 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
              <thead>
                {/* Top header row — file name groupings (colspan = # zones from that file). */}
                <tr style={{ background: "#D8DCE8" }}>
                  <th rowSpan={2} style={{ padding: 4, textAlign: "left", borderBottom: "1px solid #BBC", borderRight: "1px solid #BBC", position: "sticky", left: 0, background: "#D8DCE8" }}>Agency</th>
                  {(() => {
                    // Sort zones by source_file then name so all zones from
                    // a given file are adjacent.
                    const sorted = [...parZones].sort((a, b) => {
                      const sa = (a.source_file || "(unknown)") + "|" + (a.name || "");
                      const sb = (b.source_file || "(unknown)") + "|" + (b.name || "");
                      return sa.localeCompare(sb);
                    });
                    // Group consecutive zones with same source_file.
                    const groups = [];
                    sorted.forEach((z) => {
                      const file = z.source_file || "(unknown)";
                      const last = groups[groups.length - 1];
                      if (last && last.file === file) last.zones.push(z);
                      else groups.push({ file, zones: [z] });
                    });
                    return groups.map((g) => (
                      <th key={g.file} colSpan={g.zones.length} style={{ padding: "4px 6px", borderBottom: "1px solid #BBC", borderLeft: "2px solid #99A", fontSize: 10, textAlign: "center", color: "#445" }}>
                        {g.file}
                      </th>
                    ));
                  })()}
                </tr>
                {/* Bottom header row — zone names. */}
                <tr style={{ background: "#E8E8F0" }}>
                  {[...parZones].sort((a, b) => {
                    const sa = (a.source_file || "(unknown)") + "|" + (a.name || "");
                    const sb = (b.source_file || "(unknown)") + "|" + (b.name || "");
                    return sa.localeCompare(sb);
                  }).map((z, idx, arr) => {
                    const isFileBoundary = idx === 0 || (arr[idx - 1].source_file !== z.source_file);
                    return (
                      <th key={z.id} style={{ padding: 4, borderBottom: "1px solid #BBC", borderLeft: isFileBoundary ? "2px solid #99A" : "1px solid #DDE", maxWidth: 110, fontSize: 10 }}>{z.name}</th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {agencies.map((a) => {
                  const assigned = parAssignments[a.id] || [];
                  const toggle = async (zoneId) => {
                    const next = assigned.includes(zoneId) ? assigned.filter((x) => x !== zoneId) : [...assigned, zoneId];
                    setParAssignments({ ...parAssignments, [a.id]: next });
                    try {
                      await axios.put(`${API}/agencies/${a.id}/par`, { par_zone_ids: next });
                    } catch (e) {
                      setParAssignments({ ...parAssignments, [a.id]: assigned });
                      alert(`Failed to update ${a.name} PAR: ${e?.response?.data?.error || e.message}`);
                    }
                  };
                  const sortedZones = [...parZones].sort((a2, b2) => {
                    const sa = (a2.source_file || "(unknown)") + "|" + (a2.name || "");
                    const sb = (b2.source_file || "(unknown)") + "|" + (b2.name || "");
                    return sa.localeCompare(sb);
                  });
                  return (
                    <tr key={a.id} style={{ borderTop: "1px solid #EEE" }}>
                      <td style={{ padding: 4, position: "sticky", left: 0, background: "#FAFAFC", fontWeight: 600, borderRight: "1px solid #BBC" }}>
                        {a.name}
                        {assigned.length > 0 && <span style={{ color: "#888", fontSize: 9, marginLeft: 6 }}>({assigned.length})</span>}
                      </td>
                      {sortedZones.map((z, idx, arr) => {
                        const isFileBoundary = idx === 0 || (arr[idx - 1].source_file !== z.source_file);
                        return (
                          <td key={z.id} style={{ padding: 4, textAlign: "center", borderLeft: isFileBoundary ? "2px solid #99A" : "1px solid #EEE" }}>
                            <input
                              data-testid={`par-cell-${a.id}-${z.id}`}
                              type="checkbox"
                              checked={assigned.includes(z.id)}
                              onChange={() => toggle(z.id)} />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: "#667", fontStyle: "italic" }}>
            Tip: an agency may select zones from any number of uploaded files. The selected zones collectively define its 10-97 PAR.
          </div>
        </div>
      )}

      {uploading && <div style={{ marginTop: 8, fontSize: 12, color: "#3366AA" }}>Parsing file...</div>}
      {uploadResult && (
        <div style={{ marginTop: 8, fontSize: 12, color: uploadResult.success ? "#006600" : "#CC0000" }}>
          {uploadResult.success ? `Added ${uploadResult.count} ${uploadResult.type} boundary(ies)` : uploadResult.error}
        </div>
      )}
    </ModalShell>
  );
}


/* ─── UNIT HISTORY MODAL (calendar + session picker + PDF/print) ─── */
export function UnitHistoryModal({ onClose }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [dateSet, setDateSet] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/unit-history/calendar`, { params: { year, month: month + 1 } })
      .then((r) => setDateSet(new Set(r.data.dates || [])))
      .catch(() => setDateSet(new Set()));
    setSelectedDate(null); setSessions([]); setSelectedSession(null); setEntries([]);
  }, [year, month]);

  useEffect(() => {
    if (!selectedDate) return;
    axios.get(`${API}/admin/unit-history/by-date`, { params: { date: selectedDate } })
      .then((r) => setSessions(r.data.sessions || []))
      .catch(() => setSessions([]));
    setSelectedSession(null); setEntries([]);
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedSession) return;
    const fetch = () => {
      axios.get(`${API}/admin/unit-history/session`, {
        params: { date: selectedDate, unit_id: selectedSession.unit_id, tour: selectedSession.tour || "" },
      }).then((r) => setEntries(r.data.entries || [])).catch(() => setEntries([]));
    };
    fetch();
    // Auto-refresh every 5s for ACTIVE (not-yet-archived) sessions so new
    // status timestamps appear in the dispatcher Unit History panel
    // immediately, without needing to re-click the session.
    if (selectedSession.active) {
      const t = setInterval(fetch, 5000);
      return () => clearInterval(t);
    }
  }, [selectedSession, selectedDate]);

  const monthLabel = useMemo(() => new Date(year, month, 1).toLocaleString("en-US", { month: "long", year: "numeric" }), [year, month]);
  const grid = useMemo(() => {
    const firstDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = Array(firstDow).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);
  const goPrev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const goNext = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const fmtDateStr = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const prettyDate = selectedDate ? new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "";
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";

  const printSession = () => {
    if (!selectedSession || entries.length === 0) return;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    const rows = entries.map((e) => `<tr>
      <td style="padding:3px 8px;border-bottom:1px solid #DDD;font-family:monospace;">${fmtTime(e.timestamp)}</td>
      <td style="padding:3px 8px;border-bottom:1px solid #DDD;font-weight:600;">${(e.event || "").toUpperCase()}</td>
      <td style="padding:3px 8px;border-bottom:1px solid #DDD;">${(e.details || "").replace(/</g, "&lt;")}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>Unit History — ${selectedSession.unit_name} — ${selectedDate}</title>
      <style>body{font-family:Arial,sans-serif;margin:24px;color:#000}h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:0 0 12px;color:#444;font-weight:400}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#00BFFF;color:#FFF;padding:5px 8px;text-align:left}</style>
      </head><body><h1>UNIT HISTORY — ${selectedSession.unit_name}</h1>
      <h2>${prettyDate} · Tour: ${selectedSession.tour || "—"} · ${entries.length} entries</h2>
      <table><thead><tr><th>Time</th><th>Event</th><th>Details</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script></body></html>`);
    w.document.close();
  };

  const downloadPdf = () => {
    if (!selectedSession || entries.length === 0) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text(`UNIT HISTORY — ${selectedSession.unit_name}`, 40, 50);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(80);
    doc.text(`${prettyDate}   Tour: ${selectedSession.tour || "—"}   (${entries.length} entries)`, 40, 68);
    doc.setTextColor(0);
    let y = 96;
    doc.setFillColor(0, 191, 255); doc.rect(40, y - 14, 532, 20, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
    doc.text("Time", 48, y); doc.text("Event", 130, y); doc.text("Details", 230, y);
    doc.setTextColor(0); doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
    y += 18;
    for (const e of entries) {
      if (y > 740) { doc.addPage(); y = 60; }
      const lines = doc.splitTextToSize(e.details || "", 340);
      doc.text(fmtTime(e.timestamp), 48, y);
      doc.text((e.event || "").toUpperCase(), 130, y);
      doc.text(lines, 230, y);
      y += Math.max(14, lines.length * 12);
      doc.setDrawColor(220); doc.line(40, y - 6, 572, y - 6);
    }
    doc.save(`UnitHistory_${selectedSession.unit_name}_${selectedDate}_${(selectedSession.tour || "").replace(/\s+/g, "_") || "NA"}.pdf`);
  };

  const navBtn = { background: "#2A3A6A", border: "none", color: "#FFF", padding: "4px 10px", cursor: "pointer", fontFamily: MODAL_FONT, fontWeight: 600, fontSize: 12 };

  return (
    <ModalShell title="UNIT HISTORY" onClose={onClose} width={1000}>
      <div style={{ background: "#F5F5F5", padding: 12, display: "grid", gridTemplateColumns: "300px 260px 1fr", gap: 10, minHeight: 500 }}>
        {/* Calendar */}
        <div style={{ background: "#FFF", border: "1px solid #CCC", borderRadius: 4, padding: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button data-testid="hist-prev" onClick={goPrev} style={navBtn}>◀</button>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#333", fontFamily: MODAL_FONT }}>{monthLabel.toUpperCase()}</div>
            <button data-testid="hist-next" onClick={goNext} style={navBtn}>▶</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 10, color: "#888", marginBottom: 4, textAlign: "center", fontFamily: MODAL_FONT }}>
            {["S","M","T","W","T","F","S"].map((d,i)=> <div key={i}>{d}</div>)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {grid.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = fmtDateStr(d);
              const hasData = dateSet.has(key);
              const isSel = selectedDate === key;
              return (
                <div key={i} data-testid={`hist-day-${key}`}
                  onClick={hasData ? () => setSelectedDate(key) : undefined}
                  style={{ padding: "7px 0", textAlign: "center", fontSize: 12, borderRadius: 3,
                    background: isSel ? "#00BFFF" : hasData ? "#BEE5FF" : "#F0F0F0",
                    color: isSel ? "#000" : hasData ? "#003366" : "#999",
                    cursor: hasData ? "pointer" : "default", fontWeight: hasData ? 700 : 400 }}>{d}</div>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: "#888", marginTop: 8, fontFamily: MODAL_FONT }}>Dates with archived history shaded blue.</div>
        </div>

        {/* Sessions */}
        <div style={{ background: "#FFF", border: "1px solid #CCC", borderRadius: 4, padding: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#333", fontFamily: MODAL_FONT }}>
            {selectedDate ? `SESSIONS · ${prettyDate}` : "SELECT A DATE"}
          </div>
          {selectedDate && sessions.length === 0 && <div style={{ color: "#888", fontSize: 12 }}>No sessions.</div>}
          {sessions.map((s) => {
            const sid = s.session_id || `${s.unit_id}|${s.tour}|${s.first_ts || ""}`;
            const isSel = selectedSession && (selectedSession.session_id || `${selectedSession.unit_id}|${selectedSession.tour}|${selectedSession.first_ts || ""}`) === sid;
            return (
              <div key={sid}
                data-testid={`hist-session-${s.unit_name}-${s.tour || "na"}`}
                onClick={() => setSelectedSession(s)}
                style={{ padding: "6px 8px", marginBottom: 3, borderRadius: 3, cursor: "pointer",
                  background: isSel ? "#00BFFF" : "#F8F8F8",
                  color: isSel ? "#000" : "#222", border: "1px solid " + (isSel ? "#00BFFF" : "#DDD"),
                  fontFamily: MODAL_FONT, fontSize: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 700 }}>{s.unit_name}</div>
                  {s.active && (
                    <span data-testid={`hist-active-${s.unit_name}`}
                      style={{ fontSize: 9, fontWeight: 700, background: "#2E7A3A", color: "#FFF", padding: "2px 6px", borderRadius: 10, letterSpacing: 0.5 }}>
                      ACTIVE
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, opacity: 0.85 }}>{s.tour || "No tour"} · {s.entries} events</div>
              </div>
            );
          })}
        </div>

        {/* Detail + actions */}
        <div style={{ background: "#FFF", border: "1px solid #CCC", borderRadius: 4, padding: 8, display: "flex", flexDirection: "column", minHeight: 450 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: "#333", fontFamily: MODAL_FONT }}>
              {selectedSession ? `${selectedSession.unit_name} · ${selectedSession.tour || "No tour"}` : "SELECT A SESSION"}
            </div>
            {selectedSession && (
              <div style={{ display: "flex", gap: 6 }}>
                <button data-testid="hist-print" onClick={printSession} style={{ ...navBtn, background: "#555" }}>🖨 Print</button>
                <button data-testid="hist-pdf" onClick={downloadPdf} style={{ ...navBtn, background: "#00BFFF", color: "#000" }}>⬇ PDF</button>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflowY: "auto", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, background: "#FAFAFA", padding: 6, border: "1px solid #EEE" }}>
            {entries.length === 0 && selectedSession && <div style={{ color: "#888" }}>No entries.</div>}
            {entries.map((e, i) => (
              <div key={e.id || i} style={{ display: "flex", gap: 6, padding: "2px 0", borderBottom: "1px solid #EEE" }}>
                <span style={{ color: "#888", minWidth: 60 }}>{fmtTime(e.timestamp)}</span>
                <span style={{ color: "#CC6600", minWidth: 100, fontWeight: 700 }}>{(e.event || "").toUpperCase()}</span>
                <span style={{ color: "#000", flex: 1 }}>{e.details}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}



/* ═══════════════════════════════
   FIRE MDT BINDINGS — admin view of every IP↔unit mapping.
   Lets the dispatcher revoke a stale binding without trekking to the
   firehouse. Hits GET /api/fire-mdt/bindings (joined with unit doc)
   and DELETE /api/fire-mdt/bind?ip_address=<ip>.
   ═══════════════════════════════ */
export function FireMDTBindingsModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmIp, setConfirmIp] = useState(null);

  const fetch_ = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fire-mdt/bindings`);
      setRows(r.data || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load bindings");
    }
    setLoading(false);
  };

  useEffect(() => { fetch_(); }, []);

  const revoke = async (ip) => {
    try {
      await axios.delete(`${API}/fire-mdt/bind`, { params: { ip_address: ip } });
      setConfirmIp(null);
      fetch_();
    } catch (e) {
      setError(e?.response?.data?.error || "Revoke failed");
    }
  };

  // ISO timestamp → "MM/DD HH:MM:SS" + relative ("3m ago").
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const now = new Date();
      const ageS = Math.max(0, Math.floor((now - d) / 1000));
      let rel;
      if (ageS < 60) rel = `${ageS}s ago`;
      else if (ageS < 3600) rel = `${Math.floor(ageS / 60)}m ago`;
      else if (ageS < 86400) rel = `${Math.floor(ageS / 3600)}h ago`;
      else rel = `${Math.floor(ageS / 86400)}d ago`;
      return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${d.toTimeString().slice(0, 8)} (${rel})`;
    } catch {
      return iso;
    }
  };

  return (
    <ModalShell title="FIRE MDT BINDINGS" onClose={onClose} width={780}>
      <div style={{ marginBottom: 10, padding: 8, background: "#FFE8E8", border: "1px solid #C09060", borderRadius: 3, fontSize: 12, color: "#7A3A00" }}>
        Each row maps a Fire MDT terminal's <strong>source IP</strong> to a Fire apparatus.
        Revoking a binding logs the terminal out — the next page load will show the bind selector again.
      </div>
      {loading && <div style={{ padding: 12, color: "#888" }}>Loading…</div>}
      {error && <div style={{ padding: 8, background: "#FFD0D0", color: "#900", borderRadius: 3, marginBottom: 8 }}>{error}</div>}
      <div style={{ border: "1px solid #AAB8CC", borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "150px 130px 130px 1fr 90px", gap: 6, padding: "6px 10px", background: "#C0C8D8", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #AAB8CC" }}>
          <span>IP ADDRESS</span><span>UNIT</span><span>AGENCY</span><span>LAST SEEN</span><span style={{ textAlign: "right" }}>ACTIONS</span>
        </div>
        {rows.map((r) => {
          const u = r.unit || {};
          const isLogged = u.logged_in;
          return (
            <div key={r.ip_address} data-testid={`binding-${r.ip_address}`} style={{ display: "grid", gridTemplateColumns: "150px 130px 130px 1fr 90px", gap: 6, padding: "8px 10px", borderBottom: "1px solid #E0E4EC", fontSize: 12, alignItems: "center" }}>
              <span style={{ fontFamily: "monospace", color: "#222", fontWeight: 600 }}>{r.ip_address}</span>
              <span style={{ color: "#222", fontWeight: 700 }}>{r.unit_name || u.name || "—"}{u.unit_type ? <span style={{ display: "block", fontSize: 10, color: "#888", fontWeight: 400 }}>{u.unit_type}</span> : null}</span>
              <span style={{ color: "#555" }}>{u.agency || "—"}</span>
              <span style={{ color: "#555", fontSize: 11 }}>
                {fmtTime(r.last_seen)}
                <span style={{ display: "block", fontSize: 10, color: isLogged ? "#080" : "#A30" }}>
                  {isLogged ? "● in service" : "○ off / not logged on"}
                </span>
              </span>
              <span style={{ textAlign: "right" }}>
                {confirmIp === r.ip_address ? (
                  <span style={{ display: "inline-flex", gap: 4 }}>
                    <button data-testid={`binding-confirm-${r.ip_address}`} onClick={() => revoke(r.ip_address)} style={{ background: "#D8252B", color: "#FFF", border: "none", padding: "3px 8px", borderRadius: 2, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>YES</button>
                    <button onClick={() => setConfirmIp(null)} style={{ background: "#888", color: "#FFF", border: "none", padding: "3px 8px", borderRadius: 2, fontSize: 11, cursor: "pointer" }}>NO</button>
                  </span>
                ) : (
                  <button data-testid={`binding-revoke-${r.ip_address}`} onClick={() => setConfirmIp(r.ip_address)} style={{ background: "linear-gradient(180deg, #FFD8D8, #E0A0A0)", border: "1px solid #C06060", color: "#700", padding: "3px 8px", borderRadius: 2, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>REVOKE</button>
                )}
              </span>
            </div>
          );
        })}
        {!loading && rows.length === 0 && (
          <div style={{ padding: 16, color: "#888", fontSize: 12, textAlign: "center" }}>No Fire MDT terminals are bound yet.</div>
        )}
      </div>
    </ModalShell>
  );
}



/* ═══════════════════════════════
   CAD NUMBER SETTINGS (PER AGENCY)
   ═══════════════════════════════
   Iter 94 — Admin sets the starting CAD number per agency. The number
   the dispatcher will see on the NEXT dispatched call equals `next_value`.
   Counters reset to 0001 automatically on January 1 at NY-local midnight
   via the backend `get_next_cad_number()` helper. Manual reset button
   provided for drills / end-of-year practice runs.
*/
export function CadCountersModal({ onClose, agencies }) {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});  // agency → string
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/cad-counters`);
      setRows(r.data?.counters || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Merge agency list + existing counter rows so admin can set a start
  // value for an agency that has never dispatched yet.
  const allRows = useMemo(() => {
    const byAgency = new Map((rows || []).map((r) => [r.agency, r]));
    const out = [];
    for (const a of (agencies || [])) {
      const existing = byAgency.get(a.name);
      out.push({
        agency: a.name,
        next_value: existing?.next_value ?? 1,
        current_value: existing?.current_value ?? 0,
        year: existing?.year ?? new Date().getFullYear(),
        configured: !!existing,
      });
      byAgency.delete(a.name);
    }
    // Any leftover rows in the counters collection (e.g., legacy
    // agencies no longer in the list) still appear at the bottom.
    for (const [, r] of byAgency) {
      out.push({ ...r, configured: true });
    }
    return out;
  }, [rows, agencies]);

  const saveOne = async (agency) => {
    const raw = (edits[agency] ?? "").toString().trim();
    if (!raw) return;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 1) {
      setMsg(`Invalid starting number for ${agency}.`);
      return;
    }
    setSaving(true); setMsg("");
    try {
      await axios.put(`${API}/admin/cad-counters/${encodeURIComponent(agency)}`, { next_value: n });
      setEdits((e) => { const c = { ...e }; delete c[agency]; return c; });
      setMsg(`✓ Saved — ${agency} will start at ${n}.`);
      await load();
    } catch {
      setMsg(`Failed to save ${agency}.`);
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL agency CAD counters to 0001 right now?\n\nThis is the same effect as the automatic Jan 1 rollover.")) return;
    setSaving(true); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/cad-counters/reset-all`);
      setMsg(`✓ Reset ${r.data?.reset || 0} agencies for ${r.data?.year || ""}.`);
      await load();
    } catch {
      setMsg("Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="CAD Number Settings" onClose={onClose} width={680}>
      <div style={{ background: "#F8FAFD", padding: 12, borderRadius: 4, marginBottom: 12, fontFamily: MODAL_FONT, fontSize: 12, color: "#334", lineHeight: 1.5 }}>
        Set the next CAD number each agency will be assigned. <br />
        Each agency keeps its own independent counter. All counters reset to <b>0001</b> automatically on <b>Jan 1 at 00:00</b> (NY-local).
      </div>

      {msg && (
        <div data-testid="cad-counters-msg" style={{ padding: "6px 10px", marginBottom: 10, fontSize: 12, color: msg.startsWith("✓") ? "#085" : "#A33", background: msg.startsWith("✓") ? "#E1F5E8" : "#FDE4E4", borderRadius: 3 }}>
          {msg}
        </div>
      )}

      <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #99AABB", borderRadius: 3, background: "#FFF" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MODAL_FONT, fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#D8E0F0", color: "#234" }}>
              <th style={{ padding: 8, textAlign: "left", borderBottom: "1px solid #99AABB" }}>Agency</th>
              <th style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #99AABB", width: 110 }}>Next CAD #</th>
              <th style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #99AABB", width: 130 }}>Set Starting #</th>
              <th style={{ padding: 8, textAlign: "left", borderBottom: "1px solid #99AABB", width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ padding: 16, color: "#666", textAlign: "center" }}>Loading…</td></tr>
            )}
            {!loading && allRows.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 16, color: "#888", textAlign: "center" }}>No agencies defined.</td></tr>
            )}
            {!loading && allRows.map((r) => (
              <tr key={r.agency} data-testid={`cad-counter-row-${r.agency}`} style={{ borderBottom: "1px solid #DDE" }}>
                <td style={{ padding: 8 }}>{r.agency}</td>
                <td style={{ padding: 8, textAlign: "center", fontWeight: 700, color: r.configured ? "#234" : "#999" }}>{r.next_value}</td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <input
                    data-testid={`cad-counter-input-${r.agency}`}
                    type="number"
                    min={1}
                    value={edits[r.agency] ?? ""}
                    placeholder={String(r.next_value)}
                    onChange={(e) => setEdits((p) => ({ ...p, [r.agency]: e.target.value }))}
                    style={{ width: 90, padding: "4px 6px", border: "1px solid #99AABB", borderRadius: 2, fontFamily: MODAL_FONT, fontSize: 12, textAlign: "center", background: "#FFF", color: "#000" }}
                  />
                </td>
                <td style={{ padding: 4, textAlign: "center" }}>
                  <button
                    data-testid={`cad-counter-save-${r.agency}`}
                    disabled={saving || (edits[r.agency] ?? "").toString().trim() === ""}
                    onClick={() => saveOne(r.agency)}
                    style={{ padding: "4px 10px", background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, fontFamily: MODAL_FONT, fontSize: 11, fontWeight: 600, color: "#234", cursor: "pointer", opacity: saving || !edits[r.agency] ? 0.5 : 1 }}>
                    Set
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button
          data-testid="cad-counter-reset-all"
          onClick={resetAll}
          disabled={saving}
          style={{ padding: "6px 14px", background: "linear-gradient(180deg, #D8B0B0, #C09090)", border: "1px solid #A06060", borderRadius: 3, fontFamily: MODAL_FONT, fontSize: 12, fontWeight: 600, color: "#333", cursor: "pointer" }}>
          Reset ALL to 0001
        </button>
        <div style={{ fontFamily: MODAL_FONT, fontSize: 11, color: "#445" }}>
          Auto-reset: Jan 1, 00:00 (NY-local) every year
        </div>
      </div>
    </ModalShell>
  );
}
